// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Checkbox,
    CircularProgress,
    Paper,
    Stack,
    Tooltip,
    Badge,
    Chip,
    useTheme,
    alpha,
    InputBase,
    useMediaQuery,
    Menu,
    MenuItem
} from '@mui/material';
import {
    CloudUploadOutlined,
    DeleteOutlined,
    EditOutlined,
    PlayCircleOutlined,
    CheckCircleFilled,
    UndoOutlined,
    RightOutlined,
    RedoOutlined,
    EyeOutlined
} from '@ant-design/icons';
import heic2any from 'heic2any';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// ==============================|| LOCAL COMPONENTS & ICONS ||============================== //
import {
    CommonFilemanagerFileIcon,
    EditImageIcon,
    FileDownloadIcon,
    OrangeFolderIcon,
    EditNoteIcon,
    DocumentandFileIcon,
    ModernPdfIcon,
    ModernViewPdfIcon
} from '../CommonFilemanagerIcon';
import ImageEditor from '../ImageEditor';
import ImagePreview from '../ImagePreview';
import RenameFileForm from '../RenameFileForm';
import UpdateFileNotesform from '../UpdateFileNotesform';
import DeleteFileDialog from '../DeleteFileDialog';
import VideoPreview from '../VideoPreview';
import PdfPreview from '../PdfPreview';

// ==============================|| TYPES & API IMPORTS ||============================== //
import {
    FileManagerModule,
    FILEMANAGER_MODULE_API_MAP,
    TabularFileItem,
    PreviewImageItem,
    DirectoryMapNode,
    DirectoryNode
} from 'types/CommonFileManager/commonFileManager';
import {
    useGetDirectoryAndFiles,
    useGenerateUploadUrls,
    useGetFileUrl,
    useGetFileUrls,
    useUpdateFileNotes,
    useDeleteDirectoryAndFiles,
    useRenameFile,
    useAfterSuccessfulFileUpload
} from 'api/CommonFileManager/commonFileManager';
import { useGetFileUrl as useGetTaskFileUrl } from 'api/TaskManagement/taskManagement';
import { useGetNoteFileUrl } from 'api/TaskManagement/TaskNote/taskNote';
import { useGetTaskTemplateFileUrl } from 'api/taskTemplates';
import axios from 'axios';
import { CommonFileManagerStateContext, CommonFileManagerStateContextType } from 'contexts/FileManagerContext';
import { mutate as globalSWRMutate } from 'swr';
import useAuth from 'hooks/useAuth';
import { showSuccess, showError } from 'utils/errorHandler';
import { useGetPermissions } from 'api/authUser';
import { hasAddPermission, hasDeletePermission, hasEditPermission, hasReadPermission } from 'utils/permissions';
import {
    isImageFile,
    isVideoFile,
    cleanDirectoryPath
} from 'utils/CommonFileManager/CompressedFileManager/compressedfilemanagerfunction';
import { CompressedFilemanagerProps } from 'types/CommonFileManager/compressedFileManager';
import { formatSize } from 'utils/CommonFileManager/fileManagerFunctions';
import { useParams } from 'react-router-dom';

const EMPTY_INITIAL_FILES: any[] = [];

export const CompressedFilemanager = forwardRef((props: CompressedFilemanagerProps, ref) => {
    const {
        referenceID,
        module = FileManagerModule.NONE,
        saveMode = 'onUpload',
        height = '100%',
        minHeight,
        width = '100%',
        readonly = false,
        initialFiles = EMPTY_INITIAL_FILES,
        allowedFileTypes,
        maxFileSize = 250 * 1024 * 1024, // 250MB default
        onSave,
        showSaveButton = true,
        onChange,
        featureID,
        onDirectoriesChange,
        uploadTargetDirectoryId: propUploadTargetDirectoryId,
        saving = false,
        isLoading = false,
        allowUpload,
        allowRename,
        allowDelete: allowDeleteProp,
        allowNotes,
        allowEditImage,
        allowViewPDF,
        viewPDF,
        onUploadSuccess,
        hideDirectoryHeader = false,
        hideCheckbox = false,
        showCheckbox
    } = props;

    const shouldShowViewPDF = allowViewPDF !== false && viewPDF !== false;

    const shouldHideCheckbox = hideCheckbox || showCheckbox === false;

    const formattedHeight = useMemo(() => {
        if (height === undefined || height === null) return '100%';
        if (typeof height === 'number') return `${height}px`;
        if (typeof height === 'string' && /^\d+$/.test(height.trim())) return `${height}px`;
        return height;
    }, [height]);

    const formattedMinHeight = useMemo(() => {
        if (minHeight === undefined || minHeight === null) return undefined;
        if (typeof minHeight === 'number') return `${minHeight}px`;
        if (typeof minHeight === 'string' && /^\d+$/.test(minHeight.trim())) return `${minHeight}px`;
        return minHeight;
    }, [minHeight]);

    const theme = useTheme();
    const { user } = useAuth();
    const { permissions } = useGetPermissions();
    // Global loading/processing indicator
    const [isProcessingState, setIsProcessing] = useState<boolean>(false);
    const [processingText, setProcessingText] = useState<string>('');

    const hasView = featureID ? hasReadPermission(featureID, permissions) : true;
    const allowAdd = allowUpload !== undefined ? allowUpload : (!readonly && hasView);
    const allowDelete = allowDeleteProp !== undefined ? allowDeleteProp : (!readonly && hasView);
    const allowEdit = allowRename !== undefined ? allowRename : (!readonly && hasView);
    const allowEditNotes = allowNotes !== undefined ? allowNotes : (!readonly && hasView);
    const allowEditImageBtn = allowEditImage !== undefined ? allowEditImage : (!readonly && hasView);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const progressUpdatesRef = useRef<Record<string, { percent: number; timestamp: number }>>({});
    // API Hooks
    const moduleKey = module !== FileManagerModule.NONE ? FILEMANAGER_MODULE_API_MAP[module] : '';
    const { getDirectoryAndFiles } = useGetDirectoryAndFiles(moduleKey);
    const { generateUploadUrls } = useGenerateUploadUrls(moduleKey);
    const { getFileUrl } = useGetFileUrl(moduleKey);
    const { getFileUrls } = useGetFileUrls(moduleKey);
    const { updateFileNotes } = useUpdateFileNotes(moduleKey);
    const { deleteDirectoryAndFiles } = useDeleteDirectoryAndFiles(moduleKey);
    const { renameFile } = useRenameFile(moduleKey);
    const { afterSuccessfulFileUpload } = useAfterSuccessfulFileUpload(moduleKey);

    // Component State
    const [selectedDirectoryId, setSelectedDirectoryId] = useState<string>('files');
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [apiFilesLoading, setApiFilesLoading] = useState<boolean>(false);
    const [downloadAnchorEl, setDownloadAnchorEl] = useState<null | HTMLElement>(null);
    const isDownloadMenuOpen = Boolean(downloadAnchorEl);

    const handleDownloadMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setDownloadAnchorEl(event.currentTarget);
    };

    const handleDownloadMenuClose = () => {
        setDownloadAnchorEl(null);
    };

    const isProcessing = isProcessingState || saving || isLoading || apiFilesLoading;

    const uploadFilesViaSas = async (
        filesList: File[],
        cleanDirName: string | null,
        overrideRefID?: number,
        onUploadProgress?: (fileName: string, percent: number) => void
    ) => {
        const activeRefID = overrideRefID || referenceID;
        if (!activeRefID) return;
        const response = await generateUploadUrls({
            referenceID: activeRefID,
            directoryName: cleanDirName,
            files: filesList.map(f => f.name)
        });

        for (const file of filesList) {
            const matchingObj = response?.find((item: any) => {
                const nameInResponse = item.filename || item.fileName || item.FileName || item.Filename;
                return nameInResponse?.toLowerCase() === file.name.toLowerCase();
            });

            if (!matchingObj || !matchingObj.uploadFileUrl) {
                throw new Error(`Failed to get SAS URL for ${file.name}`);
            }

            const putResponse = await axios.put(matchingObj.uploadFileUrl, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'x-ms-blob-type': 'BlockBlob'
                },
                onUploadProgress: (progressEvent) => {
                    if (onUploadProgress) {
                        const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                        const lastUpdate = progressUpdatesRef.current[file.name];
                        const now = Date.now();
                        if (
                            percent === 0 ||
                            percent === 100 ||
                            !lastUpdate ||
                            (percent - lastUpdate.percent >= 2) ||
                            (now - lastUpdate.timestamp >= 250)
                        ) {
                            progressUpdatesRef.current[file.name] = { percent, timestamp: now };
                            onUploadProgress(file.name, percent);
                        }
                    }
                }
            });

            if (putResponse.status === 200 || putResponse.status === 201) {
                try {
                    await afterSuccessfulFileUpload({
                        referenceID: activeRefID,
                        directoryName: cleanDirName,
                        fileName: file.name
                    });
                } catch (err) {
                    console.error('AfterSuccessfulFileUpload failed:', err);
                }
            }
        }
    };

    // Item state (holds files, folders, both API-sourced and locally added)
    const [items, setItems] = useState<TabularFileItem[]>([]);
    // Selected checkboxes items
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    // Local changes tracker for 'onSaveClick' mode
    const [localAddedFiles, setLocalAddedFiles] = useState<File[]>([]);
    const [localDeletedFileNames, setLocalDeletedFileNames] = useState<string[]>([]);
    const [localUpdatedNotes, setLocalUpdatedNotes] = useState<Record<string, string>>({}); // fileName -> note
    const [localRenames, setLocalRenames] = useState<Record<string, string>>({}); // originalFullPath -> newName

    const deletedFilesRef = useRef<string[]>([]);
    const refIdRef = useRef<any>(referenceID);
    const deleteApiRef = useRef<any>(deleteDirectoryAndFiles);
    const onUploadSuccessRef = useRef<any>(onUploadSuccess);
    const { taskTemplateID } = useParams();

    useEffect(() => {
        deletedFilesRef.current = localDeletedFileNames;
    }, [localDeletedFileNames]);

    useEffect(() => {
        refIdRef.current = referenceID;
    }, [referenceID]);

    useEffect(() => {
        deleteApiRef.current = deleteDirectoryAndFiles;
    }, [deleteDirectoryAndFiles]);

    useEffect(() => {
        onUploadSuccessRef.current = onUploadSuccess;
    }, [onUploadSuccess]);

    useEffect(() => {
        return () => {
            if (saveMode === 'onUpload' && refIdRef.current && deletedFilesRef.current.length > 0) {
                const filesToDelete = [...deletedFilesRef.current];
                deleteApiRef.current({
                    referenceID: refIdRef.current,
                    directories: [],
                    files: filesToDelete
                }).then(() => {
                    if (onUploadSuccessRef.current) {
                        onUploadSuccessRef.current();
                    }
                }).catch((err: any) => {
                    console.error('Failed to execute unmount deletions:', err);
                });
            }
        };
    }, [saveMode]);

    // Context dialog states
    const [isRenameFileOpen, setIsRenameFileOpen] = useState<boolean>(false);
    const [renameFileItem, setRenameFileItem] = useState<TabularFileItem | null>(null);
    const [isNoteOpen, setIsNoteOpen] = useState<boolean>(false);
    const [activeNoteItem, setActiveNoteItem] = useState<TabularFileItem | null>(null);
    const [imageEditorOpen, setImageEditorOpen] = useState<boolean>(false);
    const [fileForEditor, setFileForEditor] = useState<TabularFileItem | null>(null);
    const [previewImages, setPreviewImages] = useState<PreviewImageItem[]>([]);
    const [previewIndex, setPreviewIndex] = useState<number>(0);
    const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
    const [deleteConfig, setDeleteConfig] = useState<any>(null);

    // Video preview states
    const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
    const [videoPreviewTitle, setVideoPreviewTitle] = useState<string>('');

    // PDF preview states
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
    const [pdfPreviewTitle, setPdfPreviewTitle] = useState<string>('');
    const [pdfPreviewFile, setPdfPreviewFile] = useState<TabularFileItem | null>(null);

    // Missing state vars (were causing TS errors)
    const [editorFileUrl, setEditorFileUrl] = useState<string | null>(null);
    const [previewOpen, setPreviewOpen] = useState<boolean>(false);
    const [imageUrlsMap, setImageUrlsMap] = useState<Record<string, string>>({});

    // Inline Note Editing States
    const [editingFileId, setEditingFileId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');

    // Context interface stub states
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [deletingItems, setDeletingItems] = useState<Record<string, { progress: number; status: 'deleting' | 'completed' }>>({});
    const [isMoveOpen, setIsMoveOpen] = useState<boolean>(false);
    const [movingItemIds, setMovingItemIds] = useState<string[]>([]);
    const [container_, _setContainer] = useState<HTMLDivElement | null>(null);
    const container = container_;
    const setContainer = (el: HTMLDivElement | null) => _setContainer(el);
    const [uploadTargetDirectoryId, setUploadTargetDirectoryId] = useState<string | null>(propUploadTargetDirectoryId || null);
    const [localFileDirectories, setLocalFileDirectories] = useState<Record<string, string>>({}); // fileName -> directoryId

    useEffect(() => {
        if (propUploadTargetDirectoryId !== undefined) {
            setUploadTargetDirectoryId(propUploadTargetDirectoryId);
        }
    }, [propUploadTargetDirectoryId]);

    const [isAddDirectoryOpen, setIsAddDirectoryOpen] = useState<boolean>(false);
    const [addDirectoryParentId, setAddDirectoryParentId] = useState<string>('files');
    const [localCreatedDirs, setLocalCreatedDirs] = useState<Record<string, { name: string; parentId: string }>>({});
    const [isRenameDirectoryOpen, setIsRenameDirectoryOpen] = useState<boolean>(false);
    const [renameDirectoryItem, setRenameDirectoryItem] = useState<TabularFileItem | null>(null);

    // Directory map and files map (mocked for simple flat directory)
    const [directoryMap, setDirectoryMap] = useState<Record<string, DirectoryMapNode>>({
        'files': { name: 'Files', parentId: null }
    });
    const [filesMap, setFilesMap] = useState<Record<string, TabularFileItem[]>>({
        'files': []
    });



    // File download URLs state map
    const [fileUrlsMap, setFileUrlsMap] = useState<Record<string, string>>({});

    // Upload queue progress simulation
    const [uploadingQueue, setUploadingQueue] = useState<Array<{
        id: string;
        name: string;
        size: string;
        progress: number;
        status: 'queued' | 'uploading' | 'completed' | 'failed';
        targetDirId: string;
        file?: File;
    }>>([]);

    // Note saving progress simulation
    const [savingNotes, setSavingNotes] = useState<Record<string, { note: string; progress: number }>>({});

    // ----------------- LOAD INITIAL FILES ----------------- //
    const loadFiles = async (isSilent = false) => {
        if (saveMode === 'onUpload' && referenceID && moduleKey) {
            if (!isSilent) {
                setApiFilesLoading(true);
            }
            try {
                const res = await getDirectoryAndFiles(referenceID);
                const apiData = res || [];
                parseAndSetFiles(apiData);
                // Update SWR cache with the fetched data and disable automatic revalidation (false)
                // This keeps parent/sibling components in sync without duplicate API calls.
                globalSWRMutate([`${moduleKey}/GetList/${referenceID}`, { method: 'GET' }], apiData, false);

            } catch (err) {
                console.error('Failed to load files from API:', err);
                showError('Could not load files from server.');
            } finally {
                if (!isSilent) {
                    setApiFilesLoading(false);
                }
            }
        } else {
            // Load initial files locally
            parseAndSetFiles(initialFiles, localAddedFiles);
        }
    };

    // Note: Mount useEffect for onUpload mode is removed to prevent duplicate API call on mount.
    // The initialFiles useEffect handles loading the files list via SWR.

    // Load initial files locally in onSaveClick mode when initialFiles changes
    useEffect(() => {
        if (saveMode === 'onSaveClick') {
            loadFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFiles, saveMode]);

    const prevDirsStrRef = useRef<string>('');
    useEffect(() => {
        if (onDirectoriesChange) {
            const dirs = Object.entries(directoryMap).map(([id, node]) => ({
                id,
                name: node.name
            }));
            const jsonStr = JSON.stringify(dirs);
            if (prevDirsStrRef.current !== jsonStr) {
                prevDirsStrRef.current = jsonStr;
                onDirectoriesChange(dirs);
            }
        }
    }, [directoryMap, onDirectoriesChange]);

    useEffect(() => {
        if (saving && localAddedFiles.length > 0) {
            setUploadingQueue(prev => {
                const existingNames = new Set(prev.map(q => q.name.toLowerCase()));
                const newQueueItems = localAddedFiles
                    .filter(file => !existingNames.has(file.name.toLowerCase()))
                    .map((file, idx) => ({
                        id: `save-upload-${Date.now()}-${idx}-${file.name}`,
                        name: file.name,
                        size: formatSize(file.size),
                        progress: 0,
                        status: 'queued' as const,
                        targetDirId: uploadTargetDirectoryId || localFileDirectories[file.name] || selectedDirectoryId || 'files'
                    }));
                if (newQueueItems.length === 0) return prev;
                return [...prev, ...newQueueItems];
            });
        }
    }, [saving, localAddedFiles, uploadTargetDirectoryId, localFileDirectories, selectedDirectoryId]);

    // Parse flat array of files into directory & file structure
    const parseAndSetFiles = (rawFiles: any[], localAdded: File[] = localAddedFiles) => {
        const parsedItems: TabularFileItem[] = [];
        const foldersAdded = new Set<string>();
        const newDirMap: Record<string, DirectoryMapNode> = {
            'files': { name: 'Files', parentId: null }
        };
        const newFilesMap: Record<string, TabularFileItem[]> = {
            'files': []
        };

        rawFiles.forEach((file: any, index: number) => {
            const fullPath = file.name || '';
            if (!fullPath) return;

            const parts = fullPath.split(/[\/\\]/);
            const isFolderOnly = fullPath.endsWith('/') || fullPath.endsWith('\\');

            // Calculate parent folder name
            let parentDirId = 'files';

            // Build folders along the path
            for (let i = 0; i < parts.length - 1; i++) {
                const folderName = parts[i];
                const folderId = parentDirId === 'files' ? `files/${folderName}` : `${parentDirId}/${folderName}`;

                if (!foldersAdded.has(folderId)) {
                    foldersAdded.add(folderId);
                    const newDirNode = {
                        id: folderId,
                        name: folderName,
                        type: 'directory' as const,
                        modifiedDate: file.modifiedOn ? new Date(file.modifiedOn).toLocaleString() : '',
                        size: '',
                        modifiedBy: file.uploadedByUserName || '',
                        userType: file.userType,
                        note: ''
                    };
                    parsedItems.push(newDirNode);
                    newDirMap[folderId] = {
                        name: folderName,
                        parentId: parentDirId,
                        modifiedDate: newDirNode.modifiedDate,
                        modifiedBy: newDirNode.modifiedBy,
                        userType: newDirNode.userType
                    };
                }
                parentDirId = folderId;
            }

            // Add the file itself if it's not just a directory placeholder
            let fileName = parts[parts.length - 1];
            if (!isFolderOnly && fileName !== '.dir' && fileName) {
                const sizeStr = formatSize(file.size || 0);

                // Apply local rename if exists
                if (saveMode === 'onSaveClick' && localRenames[fullPath]) {
                    fileName = localRenames[fullPath];
                }

                const dirParts = parts.slice(0, -1);
                const renamedFullPath = dirParts.length > 0 ? `${dirParts.join('/')}/${fileName}` : fileName;


                const noteKey1 = renamedFullPath;
                const noteKey2 = `files/${renamedFullPath}`;

                const finalNote = localUpdatedNotes[noteKey1] !== undefined
                    ? localUpdatedNotes[noteKey1]
                    : localUpdatedNotes[noteKey2] !== undefined
                        ? localUpdatedNotes[noteKey2]
                        : (file.notes || file.note || '');

                const isDeleted =
                    localDeletedFileNames.includes(fullPath) ||
                    localDeletedFileNames.includes(`files/${fullPath}`) ||
                    localDeletedFileNames.includes(renamedFullPath) ||
                    localDeletedFileNames.includes(`files/${renamedFullPath}`);

                // Check if this file was overwritten locally
                const isOverwritten = saveMode === 'onSaveClick' && localAdded.some(f => {
                    const localDirId = uploadTargetDirectoryId || localFileDirectories[f.name] || selectedDirectoryId || 'files';
                    return f.name === fileName && localDirId === parentDirId;
                });
                if (isOverwritten) {
                    return; // Skip server file because we have a local version
                }

                const fileItem: TabularFileItem = {
                    id: parentDirId === 'files' ? `file-${index}-${fullPath}` : `${parentDirId}/file-${index}-${fullPath}`,
                    name: fileName,
                    type: 'file',
                    originalName: parts[parts.length - 1],
                    originalFullPath: fullPath,
                    modifiedDate: file.modifiedOn ? new Date(file.modifiedOn).toLocaleString() : '',
                    size: sizeStr,
                    modifiedBy: file.uploadedByUserName || '',
                    note: finalNote,
                    userType: file.userType,
                    directoryPath: parentDirId === 'files'
                        ? (module === FileManagerModule.TASK ? 'Files > Task' : (module === FileManagerModule.TASK_NOTE ? 'Files > TaskNote' : 'Files'))
                        : 'Files > ' + parentDirId.replace(/^files\//, '').split('/').filter(part => !/^\d+$/.test(part)).join(' > '),
                    isDeleted: isDeleted,
                    fileID: file.fileID || file.id || file.fileId
                };
                parsedItems.push(fileItem);
                if (!newFilesMap[parentDirId]) newFilesMap[parentDirId] = [];
                newFilesMap[parentDirId].push(fileItem);
            }
        });

        // Add local added files (for onSaveClick mode)
        localAdded.forEach((file, index) => {
            const fileName = file.name;
            const dirId = uploadTargetDirectoryId || localFileDirectories[fileName] || selectedDirectoryId || 'files';
            const cleanDirName = dirId === 'files' ? '' : dirId.replace(/^files\//, '');
            const fullPath = cleanDirName ? `${cleanDirName}/${fileName}` : fileName;

            // Check if it's deleted locally
            const isLocalDeleted = localDeletedFileNames.includes(fullPath) || localDeletedFileNames.includes(`files/${fullPath}`);

            const sizeStr = formatSize(file.size);
            const noteKey1 = fullPath;
            const noteKey2 = `files/${fullPath}`;
            const finalNote = localUpdatedNotes[noteKey1] !== undefined
                ? localUpdatedNotes[noteKey1]
                : localUpdatedNotes[noteKey2] !== undefined
                    ? localUpdatedNotes[noteKey2]
                    : '';

            const fileItem: TabularFileItem = {
                id: (file as any).uniqueId || `local-file-${index}-${fileName}`,
                name: fileName,
                type: 'file',
                originalFullPath: fullPath,
                modifiedDate: new Date().toLocaleString(),
                size: sizeStr,
                note: finalNote,
                status: 'completed', // Local additions are displayed immediately
                progress: 100,
                isDeleted: isLocalDeleted,
                directoryPath: dirId === 'files'
                    ? (module === FileManagerModule.TASK ? 'Files > Task' : (module === FileManagerModule.TASK_NOTE ? 'Files > TaskNote' : 'Files'))
                    : 'Files > ' + dirId.replace(/^files\//, '').split('/').filter(part => !/^\d+$/.test(part)).join(' > ')
            };
            parsedItems.push(fileItem);
            const targetMapId = dirId || 'files';
            if (!newFilesMap[targetMapId]) newFilesMap[targetMapId] = [];
            newFilesMap[targetMapId].push(fileItem);
        });
        // Merge manually created directories
        Object.entries(localCreatedDirs).forEach(([dirId, node]) => {
            if (!newDirMap[dirId]) {
                newDirMap[dirId] = {
                    name: node.name,
                    parentId: node.parentId,
                    modifiedDate: new Date().toLocaleString(),
                    modifiedBy: user?.displayName || ''
                };
            }
            if (!newFilesMap[dirId]) {
                newFilesMap[dirId] = [];
            }
        });

        setItems(parsedItems);
        setDirectoryMap(newDirMap);
        setFilesMap(newFilesMap);
    };

    // Keep local tracking array paths in sync when parent form's target directory changes
    const prevUploadTargetDirRef = useRef<string | undefined>(uploadTargetDirectoryId);
    useEffect(() => {
        const oldDir = prevUploadTargetDirRef.current;
        const newDir = uploadTargetDirectoryId;
        prevUploadTargetDirRef.current = newDir;

        if (oldDir !== newDir) {
            const oldCleanDir = !oldDir || oldDir === 'files' ? '' : oldDir.replace(/^files\//, '');
            const newCleanDir = !newDir || newDir === 'files' ? '' : newDir.replace(/^files\//, '');

            if (oldCleanDir !== newCleanDir) {
                // Update localUpdatedNotes keys
                setLocalUpdatedNotes(prev => {
                    const next: Record<string, string> = {};
                    let changed = false;
                    const localFileNames = new Set(localAddedFiles.map(f => f.name));

                    for (const [path, note] of Object.entries(prev)) {
                        const parts = path.split('/');
                        const fileName = parts[parts.length - 1];
                        const dirPart = parts.slice(0, -1).join('/');
                        const cleanDirPart = dirPart === 'files' ? '' : dirPart;

                        if (localFileNames.has(fileName) && cleanDirPart === oldCleanDir) {
                            const newPath = newCleanDir ? `${newCleanDir}/${fileName}` : `files/${fileName}`;
                            next[newPath] = note;
                            changed = true;
                        } else {
                            next[path] = note;
                        }
                    }
                    return changed ? next : prev;
                });

                // Update localDeletedFileNames
                setLocalDeletedFileNames(prev => {
                    const localFileNames = new Set(localAddedFiles.map(f => f.name));
                    let changed = false;
                    const next = prev.map(path => {
                        const parts = path.split('/');
                        const fileName = parts[parts.length - 1];
                        const dirPart = parts.slice(0, -1).join('/');
                        const cleanDirPart = dirPart === 'files' ? '' : dirPart;

                        if (localFileNames.has(fileName) && cleanDirPart === oldCleanDir) {
                            changed = true;
                            return newCleanDir ? `${newCleanDir}/${fileName}` : `files/${fileName}`;
                        }
                        return path;
                    });
                    return changed ? next : prev;
                });

                // Update localFileDirectories
                setLocalFileDirectories(prev => {
                    const next = { ...prev };
                    let changed = false;
                    for (const [name, dir] of Object.entries(prev)) {
                        if (dir === oldDir) {
                            next[name] = newDir || 'files';
                            changed = true;
                        }
                    }
                    return changed ? next : prev;
                });
            }
        }
    }, [uploadTargetDirectoryId, localAddedFiles]);

    // Sync with initialFiles in onUpload mode when parent component's SWR updates them
    useEffect(() => {
        if (saveMode === 'onUpload' && initialFiles) {
            parseAndSetFiles(initialFiles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFiles, saveMode]);

    // Reload/sync files whenever local edits arrays change
    useEffect(() => {
        if (saveMode === 'onSaveClick') {
            loadFiles();
            if (onChange) {
                onChange({
                    addedCount: localAddedFiles.length,
                    deletedCount: localDeletedFileNames.length,
                    notesCount: Object.keys(localUpdatedNotes).length,
                    renamedCount: Object.keys(localRenames).length
                });
            }
        } else if (saveMode === 'onUpload') {
            parseAndSetFiles(initialFiles);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localAddedFiles, localDeletedFileNames, localUpdatedNotes, localRenames, initialFiles, saveMode]);

    // Check if there are any files or folders in the manager
    const hasFiles = useMemo(() => {
        return items.length > 0 || uploadingQueue.length > 0;
    }, [items, uploadingQueue]);

    const hasOrExpectsFiles = useMemo(() => {
        return items.length > 0 || uploadingQueue.length > 0 || (initialFiles && initialFiles.length > 0);
    }, [items, uploadingQueue, initialFiles]);

    // ----------------- DIRECTORY NAVIGATION & FILTERING ----------------- //
    // Extract current directory chain for path breadcrumbs
    const currentBreadcrumbs = useMemo(() => {
        if (selectedDirectoryId === 'files') return ['Files'];
        const parts = selectedDirectoryId.replace(/^files\//, '').split('/');
        return ['Files', ...parts];
    }, [selectedDirectoryId]);

    // Handle back button click
    const handleBackClick = () => {
        if (selectedDirectoryId === 'files') return;
        const lastSlash = selectedDirectoryId.lastIndexOf('/');
        if (lastSlash === -1 || lastSlash === 5) {
            setSelectedDirectoryId('files');
        } else {
            setSelectedDirectoryId(selectedDirectoryId.substring(0, lastSlash));
        }
    };

    // Filter items to show in the current folder level
    const currentItems = useMemo<TabularFileItem[]>(() => {
        // Find all active uploads flat
        const activeUploads: TabularFileItem[] = uploadingQueue
            .map(q => ({
                id: q.id,
                name: q.name,
                type: 'file',
                modifiedDate: '',
                size: q.size,
                status: q.status as 'queued' | 'uploading' | 'completed',
                progress: q.progress,
                modifiedBy: '',
                note: '',
                ...(module === FileManagerModule.INSPECTION_CALL ? {
                    directoryPath: q.targetDirId === 'files'
                        ? 'Files'
                        : 'Files > ' + q.targetDirId.replace(/^files\//, '').split('/').join(' > ')
                } : {})
            }));

        // Get all files flat
        const childItems = items.filter(item => item.type === 'file');

        // Filter out items that are currently uploading to prevent duplicate rows
        const filteredChildItems = childItems.filter(item =>
            !activeUploads.some(u => u.name.toLowerCase() === item.name.toLowerCase())
        );

        return [...filteredChildItems, ...activeUploads];
    }, [items, uploadingQueue]);

    // Upload Progress Calculations for Top Banner
    const [uploadBatchSize, setUploadBatchSize] = useState(0);

    const activeUploads = (uploadingQueue || []).filter(
        (q) => q.status === 'uploading' || q.status === 'queued' || q.status === 'completed'
    );

    useEffect(() => {
        const activeCount = activeUploads.length;
        if (activeCount > uploadBatchSize) {
            setUploadBatchSize(activeCount);
        } else if (activeCount === 0) {
            setUploadBatchSize(0);
        }
    }, [activeUploads.length, uploadBatchSize]);

    const currentUploadingItem = activeUploads.find((q) => q.status === 'uploading') ||
        activeUploads.find((q) => q.status === 'queued') ||
        activeUploads.find((q) => q.status === 'completed');

    let currentUploadIndex = 0;
    if (uploadBatchSize > 0 && currentUploadingItem) {
        const remainingActiveOrQueued = activeUploads.filter(
            (q) => q.status === 'uploading' || q.status === 'queued'
        ).length;
        currentUploadIndex = Math.max(1, uploadBatchSize - remainingActiveOrQueued + 1);
        if (currentUploadIndex > uploadBatchSize) {
            currentUploadIndex = uploadBatchSize;
        }
    }

    // Overall progress of the entire upload batch
    const completedAndRemovedCount = Math.max(0, uploadBatchSize - activeUploads.length);
    const activeProgressSum = activeUploads.reduce((sum, item) => sum + (item.progress || 0), 0);
    const totalProgressSum = (completedAndRemovedCount * 100) + activeProgressSum;

    let overallProgress = 0;
    if (uploadBatchSize > 0) {
        const rawProgress = Math.floor(totalProgressSum / uploadBatchSize);
        const hasUnfinished = activeUploads.some(q => q.status === 'uploading' || q.status === 'queued');
        overallProgress = hasUnfinished ? Math.min(99, rawProgress) : Math.min(100, rawProgress);
    }

    // ----------------- DRAG AND DROP & FILE UPLOADS ----------------- //
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (!allowAdd || isProcessing) return;
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (!allowAdd || isProcessing) return;
        const droppedFiles = e.dataTransfer.files;
        if (droppedFiles && droppedFiles.length > 0) {
            processFilesUpload(droppedFiles);
        }
    };

    const handleBrowseClick = () => {
        if (!allowAdd || isProcessing) return;
        fileInputRef.current?.click();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!allowAdd || isProcessing) return;
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            processFilesUpload(selectedFiles);
        }
        e.target.value = ''; // Reset file input value to allow uploading the same file again
    };

    const processFilesUpload = async (filesList: FileList | File[]) => {
        const filesArray = Array.from(filesList);
        const validFiles: File[] = [];

        const targetDirId = uploadTargetDirectoryId || selectedDirectoryId || 'files';
        const existingItems = filesMap[targetDirId] || [];
        const existingNames = new Set([
            ...existingItems.map(item => item.name.toLowerCase()),
            ...localAddedFiles.map(file => file.name.toLowerCase())
        ]);

        const getUniqueFileName = (fileName: string, existing: Set<string>): string => {
            if (!existing.has(fileName.toLowerCase())) {
                return fileName;
            }
            const dotIndex = fileName.lastIndexOf('.');
            const baseName = dotIndex !== -1 ? fileName.substring(0, dotIndex) : fileName;
            const extension = dotIndex !== -1 ? fileName.substring(dotIndex) : '';

            let counter = 1;
            let newName = `${baseName} (${counter})${extension}`;
            while (existing.has(newName.toLowerCase())) {
                counter++;
                newName = `${baseName} (${counter})${extension}`;
            }
            return newName;
        };

        for (const file of filesArray) {
            let processedFile = file;

            // HEIC/HEIF conversion support
            const isHeic = file.name.toLowerCase().endsWith('.heic') ||
                file.name.toLowerCase().endsWith('.heif') ||
                file.type === 'image/heic' ||
                file.type === 'image/heif';

            let nameToUse = file.name;
            if (isHeic) {
                nameToUse = file.name.replace(/\.(heic|heif)$/i, '.jpeg');
            }

            const uniqueName = getUniqueFileName(nameToUse, existingNames);
            existingNames.add(uniqueName.toLowerCase());

            if (isHeic) {
                try {
                    const convertedBlob = await heic2any({
                        blob: file,
                        toType: 'image/jpeg',
                        quality: 0.7
                    });
                    const blob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;
                    processedFile = new File([blob], uniqueName, { type: 'image/jpeg' });
                } catch (error) {
                    console.error('HEIC conversion failed:', error);
                    const fallbackName = getUniqueFileName(file.name, existingNames);
                    existingNames.add(fallbackName.toLowerCase());
                    processedFile = new File([file], fallbackName, { type: file.type });
                }
            } else {
                if (uniqueName !== file.name) {
                    processedFile = new File([file], uniqueName, { type: file.type });
                }
            }

            // Size checks
            if (processedFile.size > maxFileSize) {
                showError(`File ${processedFile.name} exceeds the ${formatSize(maxFileSize)} limit.`);
                continue;
            }

            // File types validation
            if (allowedFileTypes && allowedFileTypes.length > 0) {
                const ext = '.' + processedFile.name.split('.').pop()?.toLowerCase();
                if (!allowedFileTypes.map(t => t.toLowerCase()).includes(ext)) {
                    showError(`File type ${ext} is not allowed.`);
                    continue;
                }
            }

            (processedFile as any).uniqueId = `local-${Date.now()}-${validFiles.length}-${processedFile.name}`;
            validFiles.push(processedFile);
        }

        if (validFiles.length === 0) return;

        const directoryName = cleanDirectoryPath(targetDirId);

        if (saveMode === 'onSaveClick') {
            setLocalFileDirectories(prev => {
                const next = { ...prev };
                validFiles.forEach(f => {
                    next[f.name] = targetDirId;
                });
                return next;
            });
            const nextLocalAdded = [...localAddedFiles, ...validFiles];
            setLocalAddedFiles(nextLocalAdded);
            parseAndSetFiles(initialFiles, nextLocalAdded);
            return;
        }

        // Create initial queue items with 'queued' status
        const queueItems = validFiles.map((file, idx) => ({
            id: `upload-${Date.now()}-${idx}-${file.name}`,
            name: file.name,
            size: formatSize(file.size),
            progress: 0,
            status: 'queued' as const,
            targetDirId,
            file
        }));

        // Append all to uploadingQueue
        setUploadingQueue(prev => [...prev, ...queueItems]);

        if (saveMode === 'onUpload') {
            if (!referenceID) {
                showError('Reference ID is missing.');
                setUploadingQueue(prev => prev.filter(q => !queueItems.some(qi => qi.id === q.id)));
                return;
            }

            try {
                const response = await generateUploadUrls({
                    referenceID,
                    directoryName,
                    files: validFiles.map(f => f.name)
                });

                // Start uploads to SAS URLs sequentially one by one
                for (let index = 0; index < validFiles.length; index++) {
                    const file = validFiles[index];
                    const queueItem = queueItems[index];
                    const matchingObj = response?.find((item: any) => {
                        const nameInResponse = item.filename || item.fileName || item.FileName || item.Filename;
                        return nameInResponse?.toLowerCase() === file.name.toLowerCase();
                    });

                    if (!matchingObj || !matchingObj.uploadFileUrl) {
                        console.error(`No upload URL returned for ${file.name}`);
                        setUploadingQueue(prev => prev.map(q =>
                            q.id === queueItem.id ? { ...q, status: 'failed' as const } : q
                        ));
                        continue;
                    }

                    const sasUrl = matchingObj.uploadFileUrl;

                    // Set status to uploading
                    setUploadingQueue(prev => prev.map(q =>
                        q.id === queueItem.id ? { ...q, status: 'uploading' as const } : q
                    ));

                    try {
                        const putResponse = await axios.put(sasUrl, file, {
                            headers: {
                                'Content-Type': file.type || 'application/octet-stream',
                                'x-ms-blob-type': 'BlockBlob'
                            },
                            onUploadProgress: (progressEvent) => {
                                const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                                const lastUpdate = progressUpdatesRef.current[file.name];
                                const now = Date.now();
                                if (
                                    percent === 0 ||
                                    percent === 100 ||
                                    !lastUpdate ||
                                    (percent - lastUpdate.percent >= 2) ||
                                    (now - lastUpdate.timestamp >= 250)
                                ) {
                                    progressUpdatesRef.current[file.name] = { percent, timestamp: now };
                                    setUploadingQueue(prev => prev.map(q =>
                                        q.id === queueItem.id ? { ...q, progress: percent } : q
                                    ));
                                }
                            }
                        });

                        if (putResponse.status === 200 || putResponse.status === 201) {
                            // Mark completed immediately to show the green tick
                            setUploadingQueue(prev => prev.map(q =>
                                q.id === queueItem.id ? { ...q, progress: 100, status: 'completed' as const } : q
                            ));

                            try {
                                await afterSuccessfulFileUpload({
                                    referenceID,
                                    directoryName: directoryName || null,
                                    fileName: file.name
                                });
                            } catch (err) {
                                console.error('AfterSuccessfulFileUpload failed:', err);
                            }
                        }

                        await new Promise<void>((resolve) => {
                            setTimeout(async () => {
                                try {
                                    await loadFiles(true);
                                    setUploadingQueue(prev => prev.filter(q => q.id !== queueItem.id));
                                    showSuccess(`${file.name} uploaded successfully.`);
                                    if (onUploadSuccess) {
                                        onUploadSuccess();
                                    }
                                } catch (refreshErr) {
                                    console.error('Refresh failed:', refreshErr);
                                } finally {
                                    resolve();
                                }
                            }, 800);
                        });
                    } catch (putErr) {
                        console.error(`Direct upload failed for ${file.name}:`, putErr);
                        setUploadingQueue(prev => prev.map(q =>
                            q.id === queueItem.id ? { ...q, status: 'failed' as const } : q
                        ));
                        showError(`Failed to upload ${file.name}.`);
                    }
                }
            } catch (err) {
                console.error(err);
                setUploadingQueue(prev => prev.map(q =>
                    queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'failed' as const } : q
                ));
                showError('Failed to upload files.');
            }
        } else {
            // For onSaveClick, simulated immediate local add
            setUploadingQueue(prev => prev.map(q =>
                queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'completed' as const, progress: 100 } : q
            ));

            setTimeout(() => {
                setUploadingQueue(prev => prev.filter(q => !queueItems.some(qi => qi.id === q.id)));
                setLocalFileDirectories(prev => {
                    const next = { ...prev };
                    validFiles.forEach(f => {
                        next[f.name] = targetDirId;
                    });
                    return next;
                });
                const nextLocalAdded = [...localAddedFiles, ...validFiles];
                setLocalAddedFiles(nextLocalAdded);
                parseAndSetFiles(initialFiles, nextLocalAdded);
            }, 800);
        }
    };

    const handleRetryUpload = async (id: string) => {
        const queueItem = uploadingQueue.find(q => q.id === id);
        if (!queueItem || !queueItem.file) return;

        const file = queueItem.file;
        const targetDirId = queueItem.targetDirId;
        const directoryName = targetDirId === 'files' ? '' : targetDirId.replace(/^files\//, '');

        setUploadingQueue(prev => prev.map(q =>
            q.id === id ? { ...q, status: 'queued' as const, progress: 0 } : q
        ));

        try {
            if (!referenceID) {
                throw new Error('Reference ID is missing.');
            }

            const response = await generateUploadUrls({
                referenceID,
                directoryName: directoryName,
                files: [file.name]
            });

            const matchingObj = response?.find((item: any) => {
                const nameInResponse = item.filename || item.fileName || item.FileName || item.Filename;
                return nameInResponse?.toLowerCase() === file.name.toLowerCase();
            });

            if (!matchingObj || !matchingObj.uploadFileUrl) {
                throw new Error('No upload URL returned');
            }

            const sasUrl = matchingObj.uploadFileUrl;

            setUploadingQueue(prev => prev.map(q =>
                q.id === id ? { ...q, status: 'uploading' as const } : q
            ));

            const putResponse = await axios.put(sasUrl, file, {
                headers: {
                    'Content-Type': file.type || 'application/octet-stream',
                    'x-ms-blob-type': 'BlockBlob'
                },
                onUploadProgress: (progressEvent) => {
                    const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || file.size));
                    const lastUpdate = progressUpdatesRef.current[file.name];
                    const now = Date.now();
                    if (
                        percent === 0 ||
                        percent === 100 ||
                        !lastUpdate ||
                        (percent - lastUpdate.percent >= 2) ||
                        (now - lastUpdate.timestamp >= 250)
                    ) {
                        progressUpdatesRef.current[file.name] = { percent, timestamp: now };
                        setUploadingQueue(prev => prev.map(q =>
                            q.id === id ? { ...q, progress: percent } : q
                        ));
                    }
                }
            });

            if (putResponse.status === 200 || putResponse.status === 201) {
                // Mark completed immediately to show the green tick
                setUploadingQueue(prev => prev.map(q =>
                    q.id === id ? { ...q, progress: 100, status: 'completed' as const } : q
                ));

                try {
                    await afterSuccessfulFileUpload({
                        referenceID,
                        directoryName: directoryName || null,
                        fileName: file.name
                    });
                } catch (err) {
                    console.error('AfterSuccessfulFileUpload failed:', err);
                }
            }

            showSuccess(`${file.name} uploaded successfully.`);

            setTimeout(async () => {
                await loadFiles(true);
                setUploadingQueue(prev => prev.filter(q => q.id !== id));
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            }, 800);
        } catch (err) {
            console.error('Retry upload failed:', err);
            setUploadingQueue(prev => prev.map(q =>
                q.id === id ? { ...q, status: 'failed' as const } : q
            ));
            showError(`Failed to upload ${file.name}.`);
        }
    };

    const handleCancelUpload = (id: string) => {
        setUploadingQueue(prev => prev.filter(q => q.id !== id));
    };

    // ----------------- NOTES HANDLING ----------------- //
    const handleAddNote = (item: TabularFileItem) => {
        if (!allowEditNotes) return;
        setActiveNoteItem(item);
        setIsNoteOpen(true);
    };

    const handleNoteSubmit = async (fileId: string, note: string) => {
        let foundFile: TabularFileItem | undefined;
        let targetDirId = selectedDirectoryId;

        // Try to find file by ID in the current filesMap
        for (const [dirId, fileList] of Object.entries(filesMap)) {
            const found = fileList.find(f => f.id === fileId);
            if (found) {
                foundFile = found;
                targetDirId = dirId;
                break;
            }
        }

        // Fallback to activeNoteItem if not found in filesMap
        if (!foundFile && activeNoteItem && activeNoteItem.id === fileId) {
            foundFile = activeNoteItem;
        }

        if (!foundFile) {
            console.error('File not found to submit note:', fileId);
            return;
        }

        // Check if note has not changed
        const oldNote = (foundFile.note || '').trim();
        const newNote = (note || '').trim();
        if (oldNote === newNote) {
            return; // Skip API call and update if there are no changes
        }

        const isLocal = fileId.startsWith('local-');
        let fileFullPath = '';
        if (isLocal) {
            const fileName = foundFile.name;
            const dirId = uploadTargetDirectoryId || localFileDirectories[fileName] || selectedDirectoryId || 'files';
            const cleanDirName = dirId === 'files' ? 'files' : dirId.replace(/^files\//, '');
            fileFullPath = `${cleanDirName}/${fileName}`;
        } else {
            fileFullPath = targetDirId === 'files'
                ? `files/${foundFile.name}`
                : `${targetDirId.replace(/^files\//, '')}/${foundFile.name}`;
        }

        if (saveMode === 'onUpload') {
            if (!referenceID) { showError('Reference ID is missing.'); return; }
            setIsNoteOpen(false);
            setActiveNoteItem(null);
            try {
                // Optimistically show note immediately while API round-trip happens
                setLocalUpdatedNotes(prev => ({ ...prev, [fileFullPath]: note }));
                const apiFileName = foundFile.originalFullPath || foundFile.name;
                await updateFileNotes({ referenceID, fileName: apiFileName, notes: note });
                await loadFiles(true);
                // After fresh API data is loaded, clear the optimistic override
                setLocalUpdatedNotes(prev => {
                    const next = { ...prev };
                    delete next[fileFullPath];
                    return next;
                });
            } catch (err) {
                console.error(err);
                showError('Failed to save note.');
                throw err;
            }
        } else {
            setLocalUpdatedNotes(prev => ({ ...prev, [fileFullPath]: note }));
            setIsNoteOpen(false);
            setActiveNoteItem(null);
        }
    };

    const handleSaveNote = async (fileId: string, note: string) => {
        // Initialize progress
        setSavingNotes((prev) => ({
            ...prev,
            [fileId]: { note, progress: 10 }
        }));

        // Set interval to increment progress simulated
        const intervalId = setInterval(() => {
            setSavingNotes((prev) => {
                if (!prev[fileId]) return prev;
                const currentProgress = prev[fileId].progress;
                let nextProgress = currentProgress;

                // Slowly increment progress as time passes
                if (currentProgress < 40) {
                    nextProgress += 12;
                } else if (currentProgress < 75) {
                    nextProgress += 6;
                } else if (currentProgress < 90) {
                    nextProgress += 3;
                } else if (currentProgress < 98) {
                    nextProgress += 1;
                }

                return {
                    ...prev,
                    [fileId]: { ...prev[fileId], progress: Math.min(nextProgress, 98) }
                };
            });
        }, 150);

        try {
            await handleNoteSubmit(fileId, note);

            // On success, jump immediately to 100%
            clearInterval(intervalId);
            setSavingNotes((prev) => {
                if (!prev[fileId]) return prev;
                return {
                    ...prev,
                    [fileId]: { ...prev[fileId], progress: 100 }
                };
            });

            // Wait a brief period (300ms) for the user to see "100%" before hiding
            await new Promise((resolve) => setTimeout(resolve, 300));
        } catch (err) {
            console.error('Failed to save note:', err);
            clearInterval(intervalId);
        } finally {
            clearInterval(intervalId);
            setSavingNotes((prev) => {
                const next = { ...prev };
                delete next[fileId];
                return next;
            });
        }
    };

    const fetchFileUrl = async (refID: number | string, fileItem: TabularFileItem) => {
        const relativePath = fileItem.originalFullPath || fileItem.name;
        const fileIDStr = fileItem.fileID ? String(fileItem.fileID) : undefined;

        if (module === FileManagerModule.TASK) {
            return await useGetTaskFileUrl({
                taskID: Number(refID),
                fileID: fileIDStr,
                fileName: relativePath
            });
        }

        if (module === FileManagerModule.TASK_NOTE) {
            return await useGetNoteFileUrl({
                taskNoteID: Number(refID),
                fileID: fileIDStr,
                fileName: relativePath
            });
        }

        if (module === FileManagerModule.TASK_TEMPLATE) {
            return await useGetTaskTemplateFileUrl({
                taskTemplateID: Number(taskTemplateID),
                taskTemplateTaskID: Number(refID),
                fileID: fileIDStr,
            });
        }

        return await getFileUrl(refID, relativePath);
    };

    // ----------------- DOWNLOAD FILE ----------------- //
    const handleDownload = async (file: TabularFileItem, customRelativePath?: string, skipProcessingState = false) => {
        if (!skipProcessingState && isProcessing) return;
        // If it's a local added file
        const isLocalFile = file.id.startsWith('local-');
        if (isLocalFile) {
            const cleanName = file.name;
            const matchedLocal = localAddedFiles.find(f => f.name === cleanName);
            if (matchedLocal) {
                const blobUrl = window.URL.createObjectURL(matchedLocal);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = cleanName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(blobUrl);
                showSuccess(`Downloaded ${cleanName}`);
                return;
            }
        }

        // Server downloaded file
        if (!referenceID) {
            showError('Reference ID is missing.');
            return;
        }

        if (!skipProcessingState) {
            setIsProcessing(true);
            setProcessingText('Downloading...');
        }

        try {
            const rawUrl = await fetchFileUrl(referenceID!, file);
            const url = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.url || rawUrl?.fileUrl || rawUrl?.data?.url || rawUrl?.data?.fileUrl || '');
            if (url) {
                try {
                    // Try to download as blob (handles download naming correctly)
                    const response = await fetch(url);
                    if (!response.ok) throw new Error('Fetch failed');
                    const blob = await response.blob();
                    const blobUrl = window.URL.createObjectURL(blob);

                    const link = document.createElement('a');
                    link.href = blobUrl;
                    link.download = file.name;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(blobUrl);
                } catch (fetchError) {
                    console.warn('Blob download failed, trying direct link:', fetchError);
                    // Fallback to direct link in new tab if CORS or other error occurs
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = file.name;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
                showSuccess(`Download started for ${file.name}`);
            } else {
                throw new Error('Download URL empty');
            }
        } catch (err) {
            console.error('Download failed:', err);
            const errMsg = err instanceof Error ? err.message : String(err);
            showError(`Download failed: ${errMsg}`);
        } finally {
            if (!skipProcessingState) {
                setIsProcessing(false);
                setProcessingText('');
            }
        }
    };

    // ----------------- DELETE FILE / SELECTIONS WITH DIALOG ----------------- //
    const handleDeleteFile = (file: TabularFileItem) => {
        if (!allowDelete) return;

        const filePath = file.directoryPath
            ? (file.directoryPath === 'Files' ? `Files > ${file.name}` : `${file.directoryPath} > ${file.name}`)
            : (selectedDirectoryId === 'files' ? `Files > ${file.name}` : `Files > ${selectedDirectoryId.replace(/^files\//, '').replace(/\//g, ' > ')} > ${file.name}`);

        setDeleteConfig({
            title: 'Delete File?',
            subtitle: '',
            itemName: file.name,
            itemDescription: '',
            itemType: 'file' as const,
            isFileDelete: true,
            fileName: file.name,
            filePath,
            isMultiple: false,
            confirmAction: async () => {
                const isLocal = file.id.startsWith('local-');
                const targetId = file.id;

                setSelectedIds(prev => {
                    const next = new Set(prev);
                    next.delete(targetId);
                    return next;
                });

                setDeletingItems(prev => ({
                    ...prev,
                    [targetId]: { progress: 10, status: 'deleting' }
                }));

                let progress = 10;
                const intervalId = setInterval(() => {
                    progress += 15;
                    if (progress >= 100) {
                        progress = 100;
                        clearInterval(intervalId);
                        setDeletingItems(prev => {
                            const next = { ...prev };
                            delete next[targetId];
                            return next;
                        });
                        if (isLocal) {
                            setLocalAddedFiles(prev => prev.filter(f => f.name !== file.name));
                            setLocalFileDirectories(prev => {
                                const next = { ...prev };
                                delete next[file.name];
                                return next;
                            });
                        } else {
                            setLocalDeletedFileNames(prev => [...prev, file.originalFullPath || file.name]);
                        }
                    } else {
                        setDeletingItems(prev => ({
                            ...prev,
                            [targetId]: { progress, status: 'deleting' }
                        }));
                    }
                }, 80);
            }
        });
        setIsDeleteOpen(true);
    };

    const handleDeleteSelected = (ids: string[]) => {
        if (!allowDelete || ids.length === 0) return;
        const selectedFiles = currentItems.filter(item => ids.includes(item.id) && item.type === 'file' && canManipulateItem(item));
        const filesCount = selectedFiles.length;
        if (filesCount === 0) return;

        setDeleteConfig({
            title: 'Delete Files?',
            subtitle: '',
            itemName: 'Multiple Files',
            itemDescription: '',
            itemType: 'mixed' as const,
            isMultiple: true,
            filesCount,
            confirmAction: async () => {
                const localFilesToDelete = selectedFiles.filter(item => item.id.startsWith('local-'));
                const serverFilesToDelete = selectedFiles.filter(item => !item.id.startsWith('local-'));

                // Add all to deletingItems
                const initialDeleting: Record<string, { progress: number; status: 'deleting' }> = {};
                selectedFiles.forEach(item => {
                    initialDeleting[item.id] = { progress: 10, status: 'deleting' };
                });
                setDeletingItems(prev => ({ ...prev, ...initialDeleting }));

                let progress = 10;
                const intervalId = setInterval(() => {
                    progress += 15;
                    if (progress >= 100) {
                        progress = 100;
                        clearInterval(intervalId);
                        setDeletingItems(prev => {
                            const next = { ...prev };
                            selectedFiles.forEach(item => {
                                delete next[item.id];
                            });
                            return next;
                        });

                        localFilesToDelete.forEach(item => {
                            setLocalAddedFiles(prev => prev.filter(f => f.name !== item.name));
                            setLocalFileDirectories(prev => {
                                const next = { ...prev };
                                delete next[item.name];
                                return next;
                            });
                        });

                        serverFilesToDelete.forEach(item => {
                            const fullPath = item.originalFullPath || item.name;
                            setLocalDeletedFileNames(prev => [...prev, fullPath]);
                        });
                        setSelectedIds(new Set());
                    } else {
                        setDeletingItems(prev => {
                            const next = { ...prev };
                            selectedFiles.forEach(item => {
                                if (next[item.id]) {
                                    next[item.id] = { progress, status: 'deleting' };
                                }
                            });
                            return next;
                        });
                    }
                }, 80);
            }
        });
        setIsDeleteOpen(true);
    };

    const handleRestoreFile = (item: TabularFileItem) => {
        const fileFullPath = item.originalFullPath || item.name;
        setLocalDeletedFileNames(prev => prev.filter(path =>
            path !== fileFullPath &&
            path !== `files/${fileFullPath}`
        ));
    };

    const handleSelectRow = (id: string) => {
        if (isProcessing) return;
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        if (isProcessing) return;
        if (selectedIds.size === currentItems.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(currentItems.map(item => item.id)));
        }
    };

    // ----------------- IMAGE PREVIEW & EDITOR INTEGRATION ----------------- //
    const handleFileClick = async (file: TabularFileItem) => {

        if (isProcessing) return;
        if (file.type === 'directory') {
            setSelectedDirectoryId(file.id);
            return;
        }

        if (isVideoFile(file.name)) {
            // Show video preview
            const isLocal = file.id.startsWith('local-');
            if (isLocal) {
                const matchedLocal = localAddedFiles.find(f => f.name === file.name);
                if (matchedLocal) {
                    const localUrl = window.URL.createObjectURL(matchedLocal);
                    setVideoPreviewUrl(localUrl);
                    setVideoPreviewTitle(file.name);
                } else {
                    showError('Local video file not found.');
                }
                return;
            }

            if (referenceID) {
                try {
                    const rawUrl = await fetchFileUrl(referenceID!, file);
                    const url = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.url || rawUrl?.fileUrl || rawUrl?.data?.url || rawUrl?.data?.fileUrl || '');
                    if (url) { setVideoPreviewUrl(url); setVideoPreviewTitle(file.name); }
                } catch (err) {
                    console.error('Failed to load video:', err);
                    showError('Failed to load video preview.');
                }
            }
            return;
        }

        if (file.name.toLowerCase().endsWith('.pdf')) {
            const isLocal = file.id.startsWith('local-');
            if (isLocal) {
                const matchedLocal = localAddedFiles.find(f => f.name === file.name);
                if (matchedLocal) {
                    const localUrl = window.URL.createObjectURL(matchedLocal);
                    setPdfPreviewUrl(localUrl);
                    setPdfPreviewTitle(file.name);
                    setPdfPreviewFile(file);
                } else {
                    showError('Local PDF file not found.');
                }
                return;
            }

            if (referenceID) {
                try {
                    const rawUrl = await fetchFileUrl(referenceID!, file);
                    const url = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.url || rawUrl?.fileUrl || rawUrl?.data?.url || rawUrl?.data?.fileUrl || '');
                    if (url) {
                        setPdfPreviewUrl(url);
                        setPdfPreviewTitle(file.name);
                        setPdfPreviewFile(file);
                    }
                } catch (err) {
                    console.error('Failed to load PDF:', err);
                    showError('Failed to load PDF preview.');
                }
            }
            return;
        }

        if (!isImageFile(file.name)) {
            // Trigger download/view if not image
            handleDownload(file);
            return;
        }

        // Fetch image URL if not loaded yet
        let imageSrc = fileUrlsMap[file.id];

        if (!imageSrc) {
            const isLocal = file.id.startsWith('local-');
            if (isLocal) {
                const matched = localAddedFiles.find(f => f.name === file.name);
                if (matched) {
                    imageSrc = URL.createObjectURL(matched);
                    setFileUrlsMap(prev => ({ ...prev, [file.id]: imageSrc! }));
                }
            } else if (referenceID) {
                try {
                    const rawUrl = await fetchFileUrl(referenceID!, file);
                    imageSrc = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.url || rawUrl?.fileUrl || rawUrl?.data?.url || rawUrl?.data?.fileUrl || '');
                    if (imageSrc) {
                        setFileUrlsMap(prev => ({ ...prev, [file.id]: imageSrc }));
                    }
                } catch (err) {
                    console.error('Fetch image URL failed:', err);
                }
            }
        }

        if (imageSrc) {
            const previewObj: PreviewImageItem = {
                ...file,
                url: imageSrc
            };
            setPreviewImages([previewObj]);
            setPreviewIndex(0);
            setPreviewOpen(true);
        }
    };

    const handleEditImage = async (file: TabularFileItem) => {
        if (!allowEditImageBtn) return;
        setFileForEditor(file);

        let imageSrc = fileUrlsMap[file.id];
        if (!imageSrc) {
            const isLocal = file.id.startsWith('local-');
            if (isLocal) {
                const matched = localAddedFiles.find(f => f.name === file.name);
                if (matched) {
                    imageSrc = URL.createObjectURL(matched);
                    setFileUrlsMap(prev => ({ ...prev, [file.id]: imageSrc! }));
                }
            } else if (referenceID) {
                try {
                    const rawUrl = await fetchFileUrl(referenceID!, file);
                    imageSrc = typeof rawUrl === 'string' ? rawUrl : (rawUrl?.url || rawUrl?.fileUrl || rawUrl?.data?.url || rawUrl?.data?.fileUrl || '');
                } catch (err) {
                    console.error(err);
                }
            }
        }

        if (imageSrc) {
            setEditorFileUrl(imageSrc);
            setImageEditorOpen(true);
        } else {
            showError('Could not load image details for editing.');
        }
    };

    const handleImageEditorSave = async (savedData: any, fileName: string, isSaveAsNew?: boolean) => {
        // Convert base64 data to File object and upload
        try {
            const base64Data = typeof savedData === 'string' ? savedData : savedData.imageBase64;
            const response = await fetch(base64Data);
            const blob = await response.blob();
            const editedFile = new File([blob], fileName, { type: blob.type });

            if (saveMode === 'onUpload') {
                if (!referenceID) return;
                const directoryName = cleanDirectoryPath(selectedDirectoryId);

                setIsProcessing(true);
                setProcessingText('Saving image...');

                await uploadFilesViaSas([editedFile], directoryName);

                showSuccess('Edited image saved successfully.');
                await loadFiles(true);
            } else {
                // local save mode
                setLocalAddedFiles(prev => {
                    const filtered = prev.filter(f => f.name !== fileName);
                    return [...filtered, editedFile];
                });
            }

            if (fileForEditor) {
                setFileUrlsMap(prev => {
                    const next = { ...prev };
                    delete next[fileForEditor.id];
                    return next;
                });
            }
            setImageEditorOpen(false);
            setFileForEditor(null);
            setEditorFileUrl(null);
        } catch (err) {
            console.error('Failed to save edited image:', err);
            showError('Failed to save image edit.');
        } finally {
            setIsProcessing(false);
            setProcessingText('');
        }
    };

    // ----------------- RENAME FILE ----------------- //
    const handleEditFileRename = (file: TabularFileItem) => {
        if (!allowEdit) return;
        setRenameFileItem(file);
        setIsRenameFileOpen(true);
    };

    const handleRenameFileSubmit = async (fileId: string, newName: string) => {
        const file = renameFileItem;
        if (!file) return;
        const isLocalFile = file.id.startsWith('local-');
        if (isLocalFile) {
            setLocalAddedFiles(prev => prev.map(f => {
                if (f.name === file.name) {
                    const newF = new File([f], newName, { type: f.type });
                    (newF as any).uniqueId = (f as any).uniqueId || file.id;
                    return newF;
                }
                return f;
            }));
            setLocalFileDirectories(prev => {
                const next = { ...prev };
                if (next[file.name]) {
                    next[newName] = next[file.name];
                    delete next[file.name];
                }
                return next;
            });
            // Update keys in localUpdatedNotes for local file
            setLocalUpdatedNotes(prev => {
                const next = { ...prev };
                const dirId = uploadTargetDirectoryId || localFileDirectories[file.name] || selectedDirectoryId || 'files';
                const cleanDirName = dirId === 'files' ? '' : dirId.replace(/^files\//, '');

                const oldPath1 = cleanDirName ? `${cleanDirName}/${file.name}` : file.name;
                const oldPath2 = `files/${oldPath1}`;
                const newPath1 = cleanDirName ? `${cleanDirName}/${newName}` : newName;
                const newPath2 = `files/${newPath1}`;

                if (next[oldPath1] !== undefined) {
                    next[newPath1] = next[oldPath1];
                    delete next[oldPath1];
                }
                if (next[oldPath2] !== undefined) {
                    next[newPath2] = next[oldPath2];
                    delete next[oldPath2];
                }
                return next;
            });

        } else {
            if (saveMode === 'onUpload') {
                if (!referenceID) { showError('Reference ID is missing.'); return; }
                let directoryName = '';
                if (file.originalFullPath) {
                    const parts = file.originalFullPath.split(/[\/\\]/);
                    if (parts.length > 1) {
                        directoryName = parts.slice(0, -1).join('/') + '/';
                    }
                }
                if (directoryName.startsWith('files/')) {
                    directoryName = directoryName.substring(6);
                }

                try {
                    await renameFile(referenceID, { directoryName, sourceFileName: [file.name], newFileName: newName });
                    showSuccess(`File renamed to ${newName} successfully.`);

                    // Update keys in localUpdatedNotes for server file (optimistic updates)
                    setLocalUpdatedNotes(prev => {
                        const next = { ...prev };
                        const parts = (file.originalFullPath || '').split(/[\/\\]/);
                        const dirPart = parts.slice(0, -1).join('/');

                        const oldPath1 = dirPart ? `${dirPart}/${file.name}` : file.name;
                        const oldPath2 = `files/${oldPath1}`;
                        const newPath1 = dirPart ? `${dirPart}/${newName}` : newName;
                        const newPath2 = `files/${newPath1}`;

                        if (next[oldPath1] !== undefined) {
                            next[newPath1] = next[oldPath1];
                            delete next[oldPath1];
                        }
                        if (next[oldPath2] !== undefined) {
                            next[newPath2] = next[oldPath2];
                            delete next[oldPath2];
                        }
                        return next;
                    });

                    await loadFiles(true);
                } catch (err) {
                    console.error(err);
                    showError('Failed to rename file.');
                }
            } else {
                // saveMode === 'onSaveClick'
                const originalFullPath = file.originalFullPath || (selectedDirectoryId === 'files' ? file.name : `${selectedDirectoryId.replace(/^files\//, '')}/${file.name}`);

                setLocalRenames(prev => ({
                    ...prev,
                    [originalFullPath]: newName
                }));

                // Update keys in localUpdatedNotes for server file
                setLocalUpdatedNotes(prev => {
                    const next = { ...prev };
                    const parts = (file.originalFullPath || '').split(/[\/\\]/);
                    const dirPart = parts.slice(0, -1).join('/');

                    const oldPath1 = dirPart ? `${dirPart}/${file.name}` : file.name;
                    const oldPath2 = `files/${oldPath1}`;
                    const newPath1 = dirPart ? `${dirPart}/${newName}` : newName;
                    const newPath2 = `files/${newPath1}`;

                    if (next[oldPath1] !== undefined) {
                        next[newPath1] = next[oldPath1];
                        delete next[oldPath1];
                    }
                    if (next[oldPath2] !== undefined) {
                        next[newPath2] = next[oldPath2];
                        delete next[oldPath2];
                    }
                    // Also check if the note was stored under the originalFullPath
                    if (file.originalFullPath && next[file.originalFullPath] !== undefined) {
                        const originalNewPath = dirPart ? `${dirPart}/${newName}` : newName;
                        next[originalNewPath] = next[file.originalFullPath];
                        delete next[file.originalFullPath];
                    }
                    return next;
                });


            }
        }

        // Preview mein bhi name update karo
        setPreviewImages(prev => prev.map(img =>
            img.id === file.id ? { ...img, name: newName } : img
        ));

        setIsRenameFileOpen(false);
        setRenameFileItem(null);
    };

    const performBulkDownload = async (filesToDownload: TabularFileItem[]) => {
        if (filesToDownload.length === 0) return;



        setIsProcessing(true);
        const totalCount = filesToDownload.length;
        setProcessingText(`Downloading files (0/${totalCount})...`);

        try {
            const zip = new JSZip();
            let successCount = 0;

            const localItems = filesToDownload.filter(f => f.id.startsWith('local-'));
            const serverItems = filesToDownload.filter(f => !f.id.startsWith('local-'));

            let downloadedCount = 0;
            const updateProgress = () => {
                downloadedCount++;
                setProcessingText(`Downloading files (${Math.min(downloadedCount, totalCount)}/${totalCount})...`);
            };

            localItems.forEach(item => {
                const matchedLocal = localAddedFiles.find(f => f.name === item.name);
                if (matchedLocal) {
                    let zipPath = item.originalFullPath || item.name;
                    if (module === FileManagerModule.PROJECT && referenceID) {
                        const projectPrefix = `${referenceID}/`;
                        const filesProjectPrefix = `files/${referenceID}/`;
                        if (zipPath.startsWith(filesProjectPrefix)) {
                            zipPath = zipPath.substring(filesProjectPrefix.length);
                        } else if (zipPath.startsWith(projectPrefix)) {
                            zipPath = zipPath.substring(projectPrefix.length);
                        }
                    }
                    if (zipPath.startsWith('files/')) {
                        zipPath = zipPath.substring(6);
                    } else if (zipPath.startsWith('files\\')) {
                        zipPath = zipPath.substring(6);
                    }
                    zip.file(zipPath, matchedLocal);
                    successCount++;
                }
                updateProgress();
            });

            if (serverItems.length > 0) {
                if (!referenceID) {
                    throw new Error('Reference ID is missing.');
                }

                const relativePaths = serverItems.map(item => {
                    return item.originalFullPath || item.name;
                });

                const res = await getFileUrls(referenceID, { files: relativePaths });
                if (!res) {
                    throw new Error('Could not retrieve bulk download URLs.');
                }

                const fileUrls: string[] = [];
                if (Array.isArray(res)) {
                    res.forEach((item: any) => {
                        if (typeof item === 'string') {
                            fileUrls.push(item);
                        } else if (item && typeof item.url === 'string') {
                            fileUrls.push(item.url);
                        } else if (item && typeof item.fileUrl === 'string') {
                            fileUrls.push(item.fileUrl);
                        }
                    });
                } else if (res && typeof res === 'object') {
                    Object.values(res).forEach((val: any) => {
                        if (typeof val === 'string') {
                            fileUrls.push(val);
                        }
                    });
                }

                if (fileUrls.length > 0) {
                    setProcessingText(`Downloading files (0/${totalCount})...`);
                    await Promise.all(
                        serverItems.map(async (item, index) => {
                            const url = fileUrls[index];
                            if (url) {
                                try {
                                    const response = await fetch(url);
                                    if (response.ok) {
                                        const blob = await response.blob();
                                        let zipPath = item.originalFullPath || item.name;
                                        if (module === FileManagerModule.PROJECT && referenceID) {
                                            const projectPrefix = `${referenceID}/`;
                                            const filesProjectPrefix = `files/${referenceID}/`;
                                            if (zipPath.startsWith(filesProjectPrefix)) {
                                                zipPath = zipPath.substring(filesProjectPrefix.length);
                                            } else if (zipPath.startsWith(projectPrefix)) {
                                                zipPath = zipPath.substring(projectPrefix.length);
                                            }
                                        }
                                        if (zipPath.startsWith('files/')) {
                                            zipPath = zipPath.substring(6);
                                        } else if (zipPath.startsWith('files\\')) {
                                            zipPath = zipPath.substring(6);
                                        }
                                        zip.file(zipPath, blob);
                                        successCount++;
                                    }
                                } catch (e) {
                                    console.error(`Failed to fetch content for ${item.name}:`, e);
                                } finally {
                                    updateProgress();
                                }
                            } else {
                                updateProgress();
                            }
                        })
                    );
                }
            }

            if (successCount === 0) {
                throw new Error('Failed to download any of the selected files.');
            }

            setProcessingText('Downloading...');
            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, 'files_download.zip');
            showSuccess(`Successfully downloaded ${successCount} files as ZIP.`);
        } catch (error) {
            console.error('Bulk download error:', error);
            showError(error instanceof Error ? error.message : 'Bulk download failed. Please try again.');
        } finally {
            setIsProcessing(false);
            setProcessingText('');
        }
    };

    // ----------------- BULK DOWNLOAD ----------------- //
    const handleDownloadIndividual = async (ids: string[]) => {
        if (isProcessing) return;
        const selectedFiles = currentItems.filter(item => ids.includes(item.id) && item.type === 'file' && !item.isDeleted);
        if (selectedFiles.length === 0) return;

        try {
            setIsProcessing(true);
            setProcessingText(`Downloading ${selectedFiles.length} file(s)...`);
            for (const item of selectedFiles) {
                await handleDownload(item, undefined, true);
                await new Promise(resolve => setTimeout(resolve, 300));
            }
        } finally {
            setIsProcessing(false);
            setProcessingText('');
        }
    };

    const handleBulkDownload = async (ids: string[]) => {
        if (isProcessing) return;
        const selectedFiles = currentItems.filter(item => ids.includes(item.id) && item.type === 'file' && !item.isDeleted);
        await performBulkDownload(selectedFiles);
    };

    // ----------------- DUPLICATE NAME CHECK ----------------- //
    const getCleanDirectoryPath = (dirPath: string): string => {
        if (!dirPath) return '';
        let clean = dirPath.replace(/\\/g, '/').toLowerCase();
        if (clean === 'files') return '';
        if (clean.startsWith('files/')) {
            clean = clean.substring(6);
        }
        if (clean.endsWith('/')) {
            clean = clean.slice(0, -1);
        }
        return clean;
    };

    const checkDuplicateName = (parentId: string, nameToCheck: string, excludeId?: string): boolean => {
        let actualParentId = parentId;
        if (excludeId) {
            const fileBeingRenamed = items.find(item => item.id === excludeId);
            if (fileBeingRenamed) {
                if (fileBeingRenamed.id.startsWith('local-file-') || fileBeingRenamed.id.startsWith('save-upload-') || fileBeingRenamed.id.startsWith('upload-')) {
                    actualParentId = localFileDirectories[fileBeingRenamed.name] || selectedDirectoryId || 'files';
                } else if (fileBeingRenamed.originalFullPath) {
                    const parts = fileBeingRenamed.originalFullPath.split(/[\/\\]/);
                    if (parts.length > 1) {
                        actualParentId = 'files/' + parts.slice(0, -1).join('/');
                    } else {
                        actualParentId = 'files';
                    }
                }
            }
        }

        const siblings = filesMap[actualParentId] || [];
        const hasDuplicateInMap = siblings.some(f => f.id !== excludeId && f.name.toLowerCase() === nameToCheck.toLowerCase());
        if (hasDuplicateInMap) return true;

        // Fallback: check in flat items list
        const cleanParent = getCleanDirectoryPath(actualParentId);
        return items.some(item => {
            if (item.id === excludeId || item.isDeleted) return false;
            if (item.name.toLowerCase() !== nameToCheck.toLowerCase()) return false;

            let itemDir = '';
            if (item.id.startsWith('local-file-') || item.id.startsWith('save-upload-') || item.id.startsWith('upload-')) {
                const targetDir = localFileDirectories[item.name] || selectedDirectoryId || 'files';
                itemDir = getCleanDirectoryPath(targetDir);
            } else {
                const parts = (item.originalFullPath || '').split(/[\/\\]/);
                const dirPart = parts.slice(0, -1).join('/');
                itemDir = getCleanDirectoryPath(dirPart);
            }
            return itemDir === cleanParent;
        });
    };

    // ----------------- CONTEXT STUB HANDLERS (NOT USED IN COMPRESSED MODE) ----------------- //
    const selectDirectory = (directoryId: string) => setSelectedDirectoryId(directoryId);
    const handleDirectorySelect = (_node: DirectoryNode) => { };
    const handleDirectoryDoubleClick = (_node: DirectoryNode) => { };
    const handleBreadcrumbClick = (index: number) => {
        if (index === 0) { setSelectedDirectoryId('files'); return; }
        const segments = currentBreadcrumbs.slice(1, index + 1);
        setSelectedDirectoryId(`files/${segments.join('/')}`);
    };
    const handleDirectoryClick = (directory: TabularFileItem) => setSelectedDirectoryId(directory.id);
    const handleUploadClick = () => fileInputRef.current?.click();
    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) processFilesUpload(files);
        e.target.value = ''; // Reset file input value to allow uploading the same file again
    };
    const handleNewDirectoryClick = () => { };
    const handleNewDirectoryFromTree = (_parentDir: DirectoryNode) => { };
    const handleAddDirectorySubmit = async (_parentId: string, _folderName: string): Promise<void> => { };
    const handleEditDirectory = (_directory: TabularFileItem) => { };
    const handleRenameDirectorySubmit = async (_directoryId: string, _newName: string): Promise<void> => { };
    const handleDeleteDirectory = (_directory: TabularFileItem) => { };
    const handleEditFile = (file: TabularFileItem) => handleEditImage(file);
    const handleMoveSubmit = async (_targetDirectoryId: string): Promise<boolean> => false;
    const canManipulateItem = (item: TabularFileItem) => {
        if (readonly) return false;

        const currentUserType = user?.userType != null ? Number(user.userType) : null;
        const itemUserType = item.userType != null ? Number(item.userType) : null;

        // Local files are created by the current user in this session, so they can always manipulate them
        if (item.id.startsWith('local-')) {
            return true;
        }

        if (currentUserType === 4) {
            // Contractor can manipulate their own files or files with no userType set
            return itemUserType === 4 || itemUserType === null;
        }

        if (currentUserType === 2 && module === FileManagerModule.MUNICIPALITY) {
            // PPF user can only manipulate their own files (userType 2)
            return itemUserType === 2 || itemUserType === null;
        }
        return true;
    };
    const treeData: DirectoryNode = { id: 'files', name: 'Files', children: [] } as DirectoryNode;
    const directoryOptions: { id: string; name: string }[] = [{ id: 'files', name: 'Files' }];

    // ----------------- CONTEXT VALUE ----------------- //
    const contextValue: CommonFileManagerStateContextType = {
        selectedDirectoryId, setSelectedDirectoryId,
        isProcessing, setIsProcessing,
        processingText, setProcessingText,
        isLoading,
        apiFilesLoading,
        directoryMap, setDirectoryMap,
        filesMap, setFilesMap,
        items, setItems,
        drawerOpen, setDrawerOpen,
        treeData,
        directoryOptions,
        container, setContainer,
        uploadTargetDirectoryId, setUploadTargetDirectoryId,
        uploadingQueue: uploadingQueue as CommonFileManagerStateContextType['uploadingQueue'],
        setUploadingQueue: setUploadingQueue as unknown as CommonFileManagerStateContextType['setUploadingQueue'],
        deletingItems, setDeletingItems,
        isAddDirectoryOpen, setIsAddDirectoryOpen,
        addDirectoryParentId, setAddDirectoryParentId,
        isRenameDirectoryOpen, setIsRenameDirectoryOpen,
        renameDirectoryItem, setRenameDirectoryItem,
        isRenameFileOpen, setIsRenameFileOpen,
        renameFileItem, setRenameFileItem,
        isNoteOpen, setIsNoteOpen,
        activeNoteItem, setActiveNoteItem,
        isMoveOpen, setIsMoveOpen,
        movingItemIds, setMovingItemIds,
        imageEditorOpen, setImageEditorOpen,
        fileForEditor, setFileForEditor,
        imageUrlsMap, setImageUrlsMap,
        previewImages, setPreviewImages,
        previewIndex, setPreviewIndex,
        isDeleteOpen, setIsDeleteOpen,
        deleteConfig, setDeleteConfig,
        fileInputRef,
        selectDirectory,
        handleDirectorySelect,
        handleDirectoryDoubleClick,
        handleBreadcrumbClick,
        handleBackClick,
        handleDirectoryClick,
        handleUploadClick,
        handleFileChange,
        handleNewDirectoryClick,
        handleNewDirectoryFromTree,
        handleAddDirectorySubmit,
        handleEditDirectory,
        handleRenameDirectorySubmit,
        handleEditFileRename,
        handleRenameFileSubmit,
        handleDeleteDirectory,
        handleFileClick,
        handleEditFile,
        handleImageEditorSave,
        handleDownloadFile: handleDownload,
        handleDeleteFile,
        handleDeleteSelected,
        handleBulkDownload,
        handleAddNote,
        handleNoteSubmit,
        handleMoveSubmit,
        checkDuplicateName,
        allowAdd, allowDelete, allowEdit,
        canManipulateItem
    };

    // ----------------- EXPOSE REFS & SUBMISSIONS ----------------- //
    const handleSaveClick = async (overrideReferenceID?: number) => {
        if (readonly) return;
        setIsProcessing(true);
        try {
            const refID = overrideReferenceID || referenceID;

            const filesToUpload = localAddedFiles.filter(file => {
                const dirId = uploadTargetDirectoryId || localFileDirectories[file.name] || selectedDirectoryId || 'files';
                const cleanDirName = dirId === 'files' ? '' : dirId.replace(/^files\//, '');
                const fullPath = cleanDirName ? `${cleanDirName}/${file.name}` : file.name;
                return !(
                    localDeletedFileNames.includes(fullPath) ||
                    localDeletedFileNames.includes(`files/${fullPath}`)
                );
            });

            const actualDeletions = localDeletedFileNames.filter(path => {
                const parts = path.split('/');
                const fileName = parts[parts.length - 1];
                return !localAddedFiles.some(f => f.name === fileName);
            });

            // Set up inline progress queue if there are files to upload
            let intervalId: any = null;
            let queueItems: any[] = [];

            if (filesToUpload.length > 0) {
                const existingQueue = uploadingQueue.filter(q =>
                    filesToUpload.some(file => file.name.toLowerCase() === q.name.toLowerCase())
                );

                if (existingQueue.length > 0) {
                    queueItems = existingQueue;
                } else {
                    queueItems = filesToUpload.map((file, idx) => ({
                        id: `save-upload-${Date.now()}-${idx}-${file.name}`,
                        name: file.name,
                        size: formatSize(file.size),
                        progress: 0,
                        status: 'queued' as const,
                        targetDirId: uploadTargetDirectoryId || localFileDirectories[file.name] || selectedDirectoryId || 'files',
                        file
                    }));
                    setUploadingQueue(prev => [...prev, ...queueItems]);
                }
            }

            // =========================================================================
            // [TASK & TASK NOTE MODULES] Custom onSave Handling
            // Used specifically by TaskForm (FileManagerModule.TASK) and
            // NoteForm (FileManagerModule.TASK_NOTE) via onSave prop callback.
            // =========================================================================
            if (onSave) {
                const formattedNotes = Object.entries(localUpdatedNotes).map(([path, note]) => ({
                    name: path,
                    notes: note
                }));

                if (queueItems.length > 0) {
                    setUploadingQueue(prev => prev.map(q =>
                        queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'uploading' as const, progress: 0 } : q
                    ));
                }

                try {
                    await onSave(
                        {
                            uploadedFiles: localAddedFiles,
                            deletedFiles: localDeletedFileNames,
                            updatedNotes: formattedNotes
                        },
                        (fileName, percent) => {
                            setUploadingQueue(prev => prev.map(q => {
                                if (queueItems.some(qi => qi.id === q.id) && q.name.toLowerCase() === fileName.toLowerCase()) {
                                    const status = percent === 100 ? ('completed' as const) : ('uploading' as const);
                                    const progress = percent === 100 ? 100 : Math.min(percent, 95);
                                    return { ...q, status, progress };
                                }
                                return q;
                            }));
                        }
                    );

                    if (queueItems.length > 0) {
                        setUploadingQueue(prev => prev.map(q =>
                            queueItems.some(qi => qi.id === q.id) ? { ...q, progress: 100, status: 'completed' as const } : q
                        ));
                    }
                } catch (err) {
                    if (queueItems.length > 0) {
                        setUploadingQueue(prev => prev.map(q =>
                            queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'failed' as const } : q
                        ));
                    }
                    throw err;
                }
            } else {
                // =========================================================================
                // [DEFAULT MODULES] Standard SAS Direct Azure Blob Uploads
                // Used by Permits, Inspections, Projects, Municipalities, etc. (referenceID based)
                // =========================================================================
                if (!refID) {
                    throw new Error('Reference ID is missing.');
                }

                // 1. Deletions
                if (actualDeletions.length > 0) {
                    await deleteDirectoryAndFiles({
                        referenceID: refID,
                        directories: [],
                        files: actualDeletions
                    });
                }

                // 2. Uploads (with simulation if there are files)
                if (filesToUpload.length > 0) {
                    let isApiFinished = false;
                    let apiError: any = null;

                    const uploadsByDirectory: Record<string, File[]> = {};
                    filesToUpload.forEach(file => {
                        const dirId = uploadTargetDirectoryId || localFileDirectories[file.name] || selectedDirectoryId || 'files';
                        if (!uploadsByDirectory[dirId]) {
                            uploadsByDirectory[dirId] = [];
                        }
                        uploadsByDirectory[dirId].push(file);
                    });

                    const performUploads = async () => {
                        for (const [dirId, filesList] of Object.entries(uploadsByDirectory)) {
                            const cleanDirName = dirId === 'files' ? null : dirId.replace(/^files\//, '');
                            await uploadFilesViaSas(filesList, cleanDirName, refID, (fileName, percent) => {
                                setUploadingQueue(prev => prev.map(q => {
                                    if (queueItems.some(qi => qi.id === q.id) && q.name.toLowerCase() === fileName.toLowerCase()) {
                                        const status = percent === 100 ? ('completed' as const) : ('uploading' as const);
                                        const progress = percent === 100 ? 100 : Math.min(percent, 95);
                                        return { ...q, status, progress };
                                    }
                                    return q;
                                }));
                            });
                        }
                    };

                    setUploadingQueue(prev => prev.map(q =>
                        queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'uploading' as const, progress: 0 } : q
                    ));

                    try {
                        await performUploads();
                        setUploadingQueue(prev => prev.map(q =>
                            queueItems.some(qi => qi.id === q.id) ? { ...q, progress: 100, status: 'completed' as const } : q
                        ));
                    } catch (err) {
                        setUploadingQueue(prev => prev.map(q =>
                            queueItems.some(qi => qi.id === q.id) ? { ...q, status: 'failed' as const } : q
                        ));
                        throw err;
                    }
                }

                // 3. Renames
                if (Object.keys(localRenames).length > 0) {
                    for (const [originalPath, newName] of Object.entries(localRenames)) {
                        const parts = originalPath.split('/');
                        const originalFileName = parts[parts.length - 1];
                        const directoryName = parts.length > 1 ? parts.slice(0, -1).join('/') + '/' : '';

                        await renameFile(refID, {
                            directoryName,
                            sourceFileName: [originalFileName],
                            newFileName: newName
                        });
                    }
                }

                // 4. Notes
                const formattedNotes = Object.entries(localUpdatedNotes).map(([path, note]) => ({
                    name: path,
                    notes: note
                }));

                if (formattedNotes.length > 0) {
                    for (const noteItem of formattedNotes) {
                        const apiFileName = noteItem.name.startsWith('files/')
                            ? noteItem.name.replace(/^files\//, '')
                            : noteItem.name;
                        await updateFileNotes({
                            referenceID: refID,
                            fileName: apiFileName,
                            notes: noteItem.notes
                        });
                    }
                }
            }


            // Clear local tracking arrays on successful save
            setLocalAddedFiles([]);
            setLocalDeletedFileNames([]);
            setLocalUpdatedNotes({});
            setLocalFileDirectories({});
            setLocalRenames({});

            if (queueItems.length > 0) {
                setTimeout(() => {
                    setUploadingQueue(prev => prev.filter(q => !queueItems.some(qi => qi.id === q.id)));
                    loadFiles();
                }, 800);
            } else {
                loadFiles();
            }
        } catch (err) {
            console.error(err);
            showError('Failed to save updates.');
        } finally {
            setIsProcessing(false);
            setProcessingText('');
        }
    };

    useImperativeHandle(ref, () => ({
        save: (overrideReferenceID?: number) => handleSaveClick(overrideReferenceID),
        getItems: () => items,
        getSelectedItems: () => items.filter(i => selectedIds.has(i.id)),
        getLocalState: () => ({
            added: localAddedFiles,
            deleted: localDeletedFileNames,
            notes: localUpdatedNotes,
            renamed: localRenames
        }),
        clearState: () => {
            setLocalAddedFiles([]);
            setLocalDeletedFileNames([]);
            setLocalUpdatedNotes({});
            setLocalRenames({});
        },
        createDirectory: (folderName: string, parentId: string = 'files') => {
            const cleanFolder = folderName.trim();
            if (!cleanFolder) return;
            const cleanParentId = parentId || 'files';
            const newId = cleanParentId === 'files' ? `files/${cleanFolder}` : `${cleanParentId}/${cleanFolder}`;
            setLocalCreatedDirs(prev => {
                if (prev[newId]) return prev;
                return {
                    ...prev,
                    [newId]: { name: cleanFolder, parentId: cleanParentId }
                };
            });
            setDirectoryMap(prev => {
                if (prev[newId]) return prev;
                return {
                    ...prev,
                    [newId]: { name: cleanFolder, parentId: cleanParentId }
                };
            });
            setFilesMap(prev => {
                if (prev[newId]) return prev;
                return { ...prev, [newId]: [] };
            });
        }
    }));

    // ----------------- RENDER SUBCOMPONENTS ----------------- //
    // Dashed Dropzone
    const dropzoneSection = (
        <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleBrowseClick}
            sx={{
                border: `1px dashed ${isDragging ? theme.palette.primary.main : theme.palette.grey[300]}`,
                borderRadius: '17px',
                p: 3,
                textAlign: 'center',
                cursor: (allowAdd && !isProcessing) ? 'pointer' : 'default',
                bgcolor: isDragging ? alpha(theme.palette.primary.main, 0.04) : theme.palette.grey[50],
                transition: 'all 0.2s ease-in-out',
                borderWidth: '1px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                mb: hasFiles ? 2.5 : 0
            }}
        >
            <Stack spacing={1.5} alignItems="center" justifyContent="center">
                {(isLoading || apiFilesLoading) ? (
                    <>
                        <CircularProgress size={32} color="primary" />
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            Loading...
                        </Typography>
                    </>
                ) : allowAdd ? (
                    <>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: theme.palette.primary.main
                            }}
                        >
                            <CloudUploadOutlined style={{ fontSize: '24px' }} />
                        </Box>
                        <Typography variant="body1" fontWeight={400} color="text">
                            Drag And Drop to upload the files here.
                        </Typography>
                        <Button
                            variant="contained"
                            disabled={isProcessing}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                color: theme.palette.primary.main,
                                boxShadow: 'none',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '15px',
                                py: 1,
                                px: 2,
                                borderRadius: "10px",
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            Browse & Upload
                        </Button>
                    </>
                ) : (
                    <Typography variant="body1" fontWeight={400} color="text.secondary">
                        No files in this directory.
                    </Typography>
                )}
            </Stack>
        </Box>
    );


    return (
        <CommonFileManagerStateContext.Provider value={contextValue}>
            <Paper
                elevation={0}
                sx={{
                    width: width,
                    height: formattedMinHeight ?? 'auto',
                    minHeight: formattedMinHeight,
                    maxHeight: formattedHeight,
                    display: 'flex',
                    flexDirection: 'column',
                    border: 'none',
                    borderRadius: '16px',
                    bgcolor: 'transparent',
                    position: 'relative'
                }}
            >
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileInputChange}
                />
                {/* API and Note saving loading blocker */}
                {(apiFilesLoading || isLoading) && hasOrExpectsFiles && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            bgcolor: alpha(theme.palette.background.paper, 0.7),
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Stack spacing={2} alignItems="center">
                            <CircularProgress size={36} color="primary" />
                            {(processingText || saving) && (
                                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                                    {processingText || 'Saving...'}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Header / Title Toolbar */}
                {hasOrExpectsFiles && (
                    <Box
                        sx={{
                            pb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flexShrink: 1 }}>
                            <Typography
                                variant="subtitle1"
                                fontWeight={500}
                                sx={{
                                    fontSize: '15px',
                                    flexShrink: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.75
                                }}
                            >
                                {module === FileManagerModule.MUNICIPALITY ? (
                                    <>
                                        <DocumentandFileIcon />
                                        <span>Documents & Files</span>
                                    </>
                                ) : (
                                    'Attachments'
                                )}{' '}
                                {module !== FileManagerModule.MUNICIPALITY && (
                                    <span>{`(${currentItems.filter(i => i.type === 'file').length})`}</span>
                                )}

                            </Typography>

                            {uploadBatchSize > 0 && currentUploadingItem && (
                                <Tooltip title={`Uploading: ${currentUploadingItem.name}`} arrow placement="top">
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.8,
                                            py: 0.3,
                                            px: 1,
                                            borderRadius: '8px',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                            ml: 1.5,
                                            cursor: 'pointer',
                                            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                                            '@keyframes slideInRight': {
                                                from: { opacity: 0, transform: 'translateX(10px)' },
                                                to: { opacity: 1, transform: 'translateX(0)' }
                                            }
                                        }}
                                    >
                                        {/* Pulsing Live Dot Indicator */}
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                bgcolor: theme.palette.success.main,
                                                animation: 'blinkDot 1.5s infinite ease-in-out',
                                                '@keyframes blinkDot': {
                                                    '0%': { opacity: 0.4 },
                                                    '50%': { opacity: 1 },
                                                    '100%': { opacity: 0.4 }
                                                }
                                            }}
                                        />

                                        {/* <CloudUploadOutlined
                                            style={{
                                                color: theme.palette.primary.main,
                                                fontSize: '13px'
                                            }}
                                        /> */}

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                letterSpacing: '0.2px',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {uploadBatchSize === 1
                                                ? `Uploading`
                                                : `Uploading ${currentUploadIndex}/${uploadBatchSize}`
                                            }
                                        </Typography>

                                        <Box sx={{ display: 'inline-flex', width: '1px', height: '10px', bgcolor: alpha(theme.palette.primary.main, 0.2), mx: 0.1 }} />

                                        {overallProgress === 100 ? (
                                            <CheckCircleFilled style={{ color: theme.palette.success.main, fontSize: '20px' }} />
                                        ) : (
                                            <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                {/* Circle Track */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={100}
                                                    size={24}
                                                    thickness={3.5}
                                                    sx={{ color: alpha(theme.palette.primary.main, 0.15) }}
                                                />
                                                {/* Circle Value */}
                                                <CircularProgress
                                                    variant="determinate"
                                                    value={overallProgress}
                                                    size={24}
                                                    thickness={3.5}
                                                    sx={{
                                                        color: theme.palette.primary.main,
                                                        position: 'absolute',
                                                        left: 0,
                                                        strokeLinecap: 'round'
                                                    }}
                                                />
                                                {/* Circular Label */}
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        bottom: 0,
                                                        right: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        component="div"
                                                        sx={{
                                                            color: theme.palette.primary.main,
                                                            fontSize: '7.5px',
                                                            fontWeight: 800
                                                        }}
                                                    >
                                                        {`${overallProgress}%`}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Tooltip>
                            )}
                            {processingText && processingText.toLowerCase().includes('download') && (
                                <Tooltip title={processingText} arrow placement="top">
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.8,
                                            py: 0.3,
                                            px: 1,
                                            borderRadius: '8px',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                            ml: 1.5,
                                            cursor: 'pointer',
                                            animation: 'slideInRight 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
                                            '@keyframes slideInRight': {
                                                from: { opacity: 0, transform: 'translateX(10px)' },
                                                to: { opacity: 1, transform: 'translateX(0)' }
                                            }
                                        }}
                                    >
                                        {/* Pulsing Live Dot Indicator */}
                                        <Box
                                            sx={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                bgcolor: theme.palette.primary.main,
                                                animation: 'blinkDot 1.5s infinite ease-in-out',
                                                '@keyframes blinkDot': {
                                                    '0%': { opacity: 0.4 },
                                                    '50%': { opacity: 1 },
                                                    '100%': { opacity: 0.4 }
                                                }
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                letterSpacing: '0.2px',
                                                textTransform: 'uppercase',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {processingText.replace('...', '')}
                                        </Typography>

                                        <Box sx={{ display: 'inline-flex', width: '1px', height: '10px', bgcolor: alpha(theme.palette.primary.main, 0.2), mx: 0.1 }} />

                                        <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.primary.main }} />
                                    </Box>
                                </Tooltip>
                            )}
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {selectedIds.size > 0 && (
                                <>
                                    <Badge
                                        badgeContent={selectedIds.size}
                                        sx={{
                                            '& .MuiBadge-badge': {
                                                right: 4,
                                                top: 4,
                                                minWidth: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                padding: '0 !important',
                                                backgroundColor: theme.palette.success.main,
                                                color: '#ffffff',
                                                fontSize: '10px',
                                                zIndex: 10
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                borderRadius: '8px',
                                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                                height: '32px',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.success.main, 0.15),
                                                }
                                            }}
                                        >
                                            <Tooltip title="Download Files" arrow>
                                                <IconButton
                                                    size="small"
                                                    disabled={isProcessing}
                                                    onClick={() => handleDownloadIndividual(Array.from(selectedIds))}
                                                    sx={{
                                                        p: 0.5,
                                                        height: '100%',
                                                        color: theme.palette.success.main,
                                                        '&:hover': {
                                                            backgroundColor: 'transparent'
                                                        }
                                                    }}
                                                >
                                                    <FileDownloadIcon style={{ fontSize: '18px' }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Box
                                                sx={{
                                                    width: '1px',
                                                    height: '100%',
                                                    backgroundColor: alpha(theme.palette.success.main, 0.2),
                                                }}
                                            />
                                            <IconButton
                                                size="small"
                                                disabled={isProcessing}
                                                onClick={handleDownloadMenuClick}
                                                sx={{
                                                    p: 0.5,
                                                    height: '100%',
                                                    color: theme.palette.success.main,
                                                    '&:hover': {
                                                        backgroundColor: 'transparent'
                                                    }
                                                }}
                                            >
                                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M5 6L0 0H10L5 6Z" fill={theme.palette.success.main} />
                                                </svg>
                                            </IconButton>
                                        </Box>
                                    </Badge>
                                    <Menu
                                        id="download-split-button-menu-compressed"
                                        anchorEl={downloadAnchorEl}
                                        open={isDownloadMenuOpen}
                                        onClose={handleDownloadMenuClose}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        slotProps={{
                                            paper: {
                                                sx: {
                                                    mt: 0.5,
                                                    minWidth: 160,
                                                    boxShadow: theme.shadows[1],
                                                    '& .MuiMenu-list': {
                                                        p: 0.5
                                                    }
                                                }
                                            }
                                        }}
                                    >
                                        <MenuItem
                                            onClick={() => {
                                                handleDownloadIndividual(Array.from(selectedIds));
                                                handleDownloadMenuClose();
                                            }}
                                            sx={{
                                                borderRadius: '6px',
                                                py: 1,
                                                px: 1.5,
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: theme.palette.text.primary
                                            }}
                                        >
                                            Download as Files
                                        </MenuItem>
                                        <MenuItem
                                            onClick={() => {
                                                handleBulkDownload(Array.from(selectedIds));
                                                handleDownloadMenuClose();
                                            }}
                                            sx={{
                                                borderRadius: '6px',
                                                py: 1,
                                                px: 1.5,
                                                fontSize: '14px',
                                                fontWeight: 500,
                                                color: theme.palette.text.primary
                                            }}
                                        >
                                            Download as ZIP
                                        </MenuItem>
                                    </Menu>
                                </>
                            )}
                            {allowAdd && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<CloudUploadOutlined style={{ fontSize: '18px' }} />}
                                    disabled={isProcessing}
                                    onClick={handleBrowseClick}
                                    sx={{
                                        textTransform: 'none',
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        color: theme.palette.primary.main,
                                        boxShadow: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        fontSize: '13px',
                                        py: 0.8,
                                        px: 1.5,
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Upload
                                </Button>
                            )}
                        </Stack>
                    </Box>
                )}

                {/* Main Area Wrapper */}
                <Box sx={{ flexGrow: 1, p: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {!hasOrExpectsFiles && dropzoneSection}

                    {hasOrExpectsFiles && (
                        <Stack spacing={1.5}>


                            {currentItems.length === 0 ? (
                                <Box sx={{ py: 4, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No files or folders in this directory.
                                    </Typography>
                                </Box>
                            ) : (() => {
                                // Group files by directoryPath (only for saved files); local/uploading files go into a special group
                                const groups: Array<{ groupKey: string; height?: string | number; minHeight?: string | number; label: string | null; items: TabularFileItem[] }> = [];
                                const groupMap = new Map<string, TabularFileItem[]>();

                                const isInspectionCall = module === FileManagerModule.INSPECTION_CALL;

                                currentItems.forEach(item => {
                                    const isNew = item.id.startsWith('local-file-') || item.status === 'completed';
                                    const isUploading = item.status === 'uploading' || item.status === 'queued';
                                    const defaultPath = module === FileManagerModule.TASK
                                        ? 'Files > Task'
                                        : (module === FileManagerModule.TASK_NOTE ? 'Files > TaskNote' : 'Files');
                                    const key = (!isInspectionCall && (isNew || isUploading)) ? '__local__' : (item.directoryPath && item.directoryPath !== 'Files' ? item.directoryPath : defaultPath);
                                    if (!groupMap.has(key)) {
                                        groupMap.set(key, []);
                                    }
                                    groupMap.get(key)!.push(item);
                                });

                                groupMap.forEach((groupItems, key) => {
                                    groups.push({
                                        groupKey: key,
                                        label: key === '__local__' ? null : key,
                                        items: groupItems
                                    });
                                });

                                // Sort groups: saved groups (with label) first sorted by label, then local at end
                                groups.sort((a, b) => {
                                    if (a.groupKey === '__local__') return 1;
                                    if (b.groupKey === '__local__') return -1;
                                    return (a.label || '').localeCompare(b.label || '');
                                });

                                return groups.map(group => (
                                    <React.Fragment key={group.groupKey}>
                                        {/* Folder Group Header — only for saved files with a path */}
                                        {group.label !== null && !hideDirectoryHeader && (
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                sx={{ mt: 0.5, mb: 0.5, px: 0.5 }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    {/* Orange Folder Icon (SVG inline) */}
                                                    <OrangeFolderIcon />
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontSize: '14px',
                                                            fontWeight: 400,
                                                            color: theme.palette.text.primary,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5
                                                        }}
                                                    >
                                                        {group.label.split(' > ').map((part, partIndex) => (
                                                            <React.Fragment key={partIndex}>
                                                                {partIndex > 0 && (
                                                                    <RightOutlined style={{ fontSize: '13px' }} />
                                                                )}
                                                                <span>{part}</span>
                                                            </React.Fragment>
                                                        ))}
                                                    </Typography>
                                                </Stack>
                                                {/* Download all files in this group */}
                                                <Tooltip title={`Download all in ${group.label}`}>
                                                    <IconButton
                                                        size="small"
                                                        disabled={isProcessing}
                                                        onClick={() => {
                                                            const groupFiles = group.items.filter(f => f.type === 'file');
                                                            performBulkDownload(groupFiles);
                                                        }}
                                                        sx={{
                                                            color: theme.palette.success.main,
                                                            '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) }
                                                        }}
                                                    >
                                                        <FileDownloadIcon style={{ fontSize: '18px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        )}

                                        {/* Files in this group */}
                                        {group.items.map((item) => {
                                            const isSelected = selectedIds.has(item.id);
                                            const isNew = item.id.startsWith('local-file-') || item.status === 'completed';
                                            const isUploading = item.status === 'uploading' || item.status === 'queued';
                                            const isUploadingOrCompleting = isUploading || (item.status === 'completed' && (item.id.startsWith('upload-') || item.id.startsWith('save-upload-')));

                                            return (
                                                <Stack
                                                    key={item.id}
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1.5}
                                                    sx={{ width: '100%' }}
                                                >
                                                    {!shouldHideCheckbox && (
                                                        <Checkbox
                                                            checked={isSelected}
                                                            onChange={() => handleSelectRow(item.id)}
                                                            disabled={isUploadingOrCompleting || item.isDeleted || deletingItems[item.id] !== undefined || isProcessing}
                                                            sx={{
                                                                p: 0,
                                                                color: theme.palette.grey[300],
                                                                '&.Mui-checked': {
                                                                    color: theme.palette.primary.main
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            p: 1,
                                                            border: `1px solid ${theme.palette.divider}`,
                                                            borderRadius: '10px',
                                                            bgcolor: 'background.paper',
                                                            flexGrow: 1,
                                                            minWidth: 0,
                                                            '&:hover': {
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                                            },
                                                            transition: 'all 0.15s ease-in-out'
                                                        }}
                                                    >
                                                        {/* Left: File/Folder Icon + Name/Meta Details */}
                                                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                                                            <Box
                                                                onClick={() => handleFileClick(item)}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    bgcolor: '#F5F5F5',
                                                                    borderRadius: '8px',
                                                                    width: 35,
                                                                    height: 35,
                                                                    flexShrink: 0
                                                                }}
                                                            >
                                                                <CommonFilemanagerFileIcon name={item.name} type={item.type} />
                                                            </Box>

                                                            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                    sx={{
                                                                        width: 'fit-content',
                                                                        position: 'relative',
                                                                        pr: (item.type === 'file' && allowEdit && canManipulateItem(item) && !isUploadingOrCompleting) ? '28px' : 0,
                                                                        '& .edit-button': {
                                                                            opacity: isMobile ? 1 : 0,
                                                                            transition: 'opacity 0.2s ease-in-out'
                                                                        },
                                                                        '&:hover .edit-button': {
                                                                            opacity: 1
                                                                        }
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        onClick={() => {
                                                                            if (item.isDeleted || deletingItems[item.id] !== undefined) return;
                                                                            handleFileClick(item);
                                                                        }}
                                                                        sx={{
                                                                            fontWeight: 500,
                                                                            fontSize: '13.5px',
                                                                            cursor: (item.isDeleted || deletingItems[item.id] !== undefined) ? 'default' : 'pointer',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            maxWidth: '220px',
                                                                            display: 'block',
                                                                            textDecoration: (item.isDeleted || deletingItems[item.id] !== undefined) ? 'line-through' : 'none',
                                                                            color: (item.isDeleted || deletingItems[item.id] !== undefined || item.status === 'queued') ? theme.palette.text.disabled : theme.palette.text.primary
                                                                        }}
                                                                    >
                                                                        {item.name.split(/[\/\\]/).pop()}
                                                                    </Typography>
                                                                    {isNew && (
                                                                        <Chip
                                                                            label="New"
                                                                            variant="outlined"
                                                                            color="primary"
                                                                            sx={{
                                                                                fontSize: '11px',
                                                                                height: '20px',
                                                                                fontWeight: 400,
                                                                                borderWidth: '1.5px',
                                                                                borderRadius: '6px',
                                                                                borderColor: 'primary.main',
                                                                                '& .MuiChip-label': {
                                                                                    px: 0.9,
                                                                                    py: 0.4
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                    {item.type === 'file' && allowEdit && canManipulateItem(item) && !isUploadingOrCompleting && !item.isDeleted && deletingItems[item.id] === undefined && (
                                                                        <Tooltip title="Rename File" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                className="edit-button"
                                                                                disabled={isProcessing}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleEditFileRename(item);
                                                                                }}
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    right: 0,
                                                                                    top: '50%',
                                                                                    transform: 'translateY(-50%)',
                                                                                    p: 0.5,
                                                                                    color: theme.palette.primary.main,
                                                                                    '&:hover': {
                                                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <EditOutlined style={{ fontSize: '15px' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Stack>

                                                                {/* Subtitle details: size & notes */}
                                                                {item.type === 'file' && (
                                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.2 }}>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px', flexShrink: 0, textDecoration: (item.isDeleted || deletingItems[item.id] !== undefined) ? 'line-through' : 'none' }}>
                                                                            {item.size}
                                                                        </Typography>
                                                                        {isUploadingOrCompleting && item.status !== 'completed' && (
                                                                            <Typography variant="caption" sx={{ fontSize: '12px', color: theme.palette.primary.main, fontWeight: 500 }}>
                                                                                • {item.status === 'queued' ? 'Queued' : (item.progress && item.progress >= 95 ? 'Processing file...' : 'Uploading...')}
                                                                            </Typography>
                                                                        )}
                                                                        {!isUploadingOrCompleting && !item.isDeleted && deletingItems[item.id] === undefined && (
                                                                            <Stack spacing={0.3}>
                                                                                {editingFileId === item.id ? (
                                                                                    <InputBase
                                                                                        value={editValue}
                                                                                        onChange={(e) => {
                                                                                            const val = e.target.value;
                                                                                            setEditValue(val);
                                                                                            if (val.length >= 50) {
                                                                                                handleAddNote && handleAddNote({ ...item, note: val });
                                                                                                setEditingFileId(null);
                                                                                            }
                                                                                        }}
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter') {
                                                                                                e.preventDefault();
                                                                                                handleSaveNote(item.id, editValue);
                                                                                                setEditingFileId(null);
                                                                                            } else if (e.key === 'Escape') {
                                                                                                setEditingFileId(null);
                                                                                            }
                                                                                        }}
                                                                                        onBlur={() => {
                                                                                            handleSaveNote(item.id, editValue);
                                                                                            setEditingFileId(null);
                                                                                        }}
                                                                                        autoFocus
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                        sx={{
                                                                                            fontSize: '11px',
                                                                                            fontWeight: 400,
                                                                                            color: theme.palette.text.primary,
                                                                                            padding: 0,
                                                                                            height: '16px',
                                                                                            width: '100%',
                                                                                            maxWidth: '220px',
                                                                                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                                                                                            mt: '5px',
                                                                                            '& input': {
                                                                                                padding: 0,
                                                                                                fontSize: '11px',
                                                                                                lineHeight: 1.2
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                ) : savingNotes[item.id] !== undefined ? (
                                                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0, mb: 0, minHeight: '22px' }}>
                                                                                        <Typography
                                                                                            variant="body1"
                                                                                            sx={{
                                                                                                color: theme.palette.text.secondary,
                                                                                                fontSize: '13px',
                                                                                                fontWeight: 400,
                                                                                                fontStyle: 'italic',
                                                                                                lineHeight: 1.5,
                                                                                                whiteSpace: 'nowrap',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                                maxWidth: '200px',
                                                                                                display: 'block'
                                                                                            }}
                                                                                        >
                                                                                            {savingNotes[item.id].note}
                                                                                        </Typography>
                                                                                        <CircularProgress size={16} />
                                                                                    </Stack>
                                                                                ) : item.note ? (
                                                                                    <Stack
                                                                                        direction="row"
                                                                                        spacing={0.5}
                                                                                        alignItems="center"
                                                                                        sx={{
                                                                                            mt: '5px',
                                                                                            width: 'fit-content',
                                                                                            position: 'relative',
                                                                                            pr: (allowEditNotes && canManipulateItem(item)) ? '28px' : 0,
                                                                                            '& .edit-button': {
                                                                                                opacity: isMobile ? 1 : 0,
                                                                                                transition: 'opacity 0.2s ease-in-out'
                                                                                            },
                                                                                            '&:hover .edit-button': {
                                                                                                opacity: 1
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="body1"
                                                                                            onClick={(e) => {
                                                                                                if (isProcessing) return;
                                                                                                if (allowEditNotes && canManipulateItem(item)) {
                                                                                                    e.stopPropagation();
                                                                                                    if (item.note && item.note.length >= 50) {
                                                                                                        handleAddNote && handleAddNote(item);
                                                                                                    } else {
                                                                                                        setEditingFileId(item.id);
                                                                                                        setEditValue(item.note || '');
                                                                                                    }
                                                                                                }
                                                                                            }}
                                                                                            sx={{
                                                                                                cursor: (allowEditNotes && canManipulateItem(item) && !isProcessing) ? 'pointer' : 'default',
                                                                                                fontSize: '13px',
                                                                                                fontWeight: 400,
                                                                                                fontStyle: 'italic',
                                                                                                lineHeight: 1.5,
                                                                                                whiteSpace: 'nowrap',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                                maxWidth: '200px',
                                                                                                display: 'block',
                                                                                                mt: 0,
                                                                                                color: theme.palette.text.secondary
                                                                                            }}
                                                                                        >
                                                                                            {item.note}
                                                                                        </Typography>
                                                                                        {allowEditNotes && canManipulateItem(item) && (
                                                                                            <Tooltip title="Edit Note" arrow>
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    className="edit-button"
                                                                                                    disabled={isProcessing}
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        if (item.note && item.note.length >= 50) {
                                                                                                            handleAddNote && handleAddNote(item);
                                                                                                        } else {
                                                                                                            setEditingFileId(item.id);
                                                                                                            setEditValue(item.note || '');
                                                                                                        }
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        position: 'absolute',
                                                                                                        right: 0,
                                                                                                        top: '50%',
                                                                                                        transform: 'translateY(-50%)',
                                                                                                        p: 0.25,
                                                                                                        color: theme.palette.primary.main,
                                                                                                        '&:hover': {
                                                                                                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                                                                        }
                                                                                                    }}
                                                                                                >
                                                                                                    <EditNoteIcon />
                                                                                                </IconButton>
                                                                                            </Tooltip>
                                                                                        )}
                                                                                    </Stack>
                                                                                ) : (
                                                                                    allowEditNotes && canManipulateItem(item) && (
                                                                                        <Typography
                                                                                            variant="body1"
                                                                                            onClick={(e) => {
                                                                                                if (isProcessing) return;
                                                                                                e.stopPropagation();
                                                                                                setEditingFileId(item.id);
                                                                                                setEditValue('');
                                                                                            }}
                                                                                            sx={{
                                                                                                color: isProcessing ? theme.palette.text.disabled : theme.palette.primary.main,
                                                                                                cursor: isProcessing ? 'default' : 'pointer',
                                                                                                fontSize: '11px',
                                                                                                fontWeight: 400,
                                                                                                textDecoration: 'underline',
                                                                                                lineHeight: 1.2,
                                                                                                whiteSpace: 'nowrap',
                                                                                                overflow: 'hidden',
                                                                                                textOverflow: 'ellipsis',
                                                                                                maxWidth: '200px',
                                                                                                display: 'block',
                                                                                                mt: '5px'
                                                                                            }}
                                                                                        >
                                                                                            Click here to add Note
                                                                                        </Typography>
                                                                                    )
                                                                                )}
                                                                            </Stack>
                                                                        )}
                                                                    </Stack>
                                                                )}
                                                            </Box>
                                                        </Stack>

                                                        {/* Right: Actions */}
                                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ ml: 1, pr: (isUploadingOrCompleting || item.status === 'failed' || deletingItems[item.id] !== undefined) ? 0.5 : 0 }}>
                                                            {deletingItems[item.id] !== undefined ? (
                                                                <CircularProgressWithLabel
                                                                    value={deletingItems[item.id]?.progress || 0}
                                                                    color={theme.palette.error.main}
                                                                />
                                                            ) : isUploadingOrCompleting ? (
                                                                item.status === 'completed' ? (
                                                                    <CheckCircleFilled style={{ color: theme.palette.success.main, fontSize: '22px', display: 'block', marginRight: '4px' }} />
                                                                ) : (
                                                                    <UploadProgressCircle
                                                                        status={item.status as 'queued' | 'uploading' | 'completed' | 'failed'}
                                                                        progress={item.progress || 0}
                                                                    />
                                                                )
                                                            ) : item.status === 'failed' ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <Tooltip title="Retry Upload" arrow>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRetryUpload && handleRetryUpload(item.id)}
                                                                            sx={{
                                                                                color: theme.palette.primary.main,
                                                                                p: 0.5,
                                                                                '&:hover': {
                                                                                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                                                }
                                                                            }}
                                                                        >
                                                                            <RedoOutlined style={{ fontSize: '15px' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Delete" arrow>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleCancelUpload && handleCancelUpload(item.id)}
                                                                            sx={{
                                                                                color: theme.palette.error.main,
                                                                                p: 0.5,
                                                                                '&:hover': {
                                                                                    backgroundColor: alpha(theme.palette.error.main, 0.08)
                                                                                }
                                                                            }}
                                                                        >
                                                                            <DeleteOutlined style={{ fontSize: '15px' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            ) : item.isDeleted ? (
                                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: theme.palette.error.main,
                                                                            fontWeight: 600,
                                                                            fontSize: '11px',
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.5px',
                                                                            pr: 0.5
                                                                        }}
                                                                    >
                                                                        Deleted
                                                                    </Typography>
                                                                    <Tooltip title="Restore File">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRestoreFile(item)}
                                                                            disabled={isProcessing}
                                                                            sx={{
                                                                                color: theme.palette.success.main,
                                                                                p: 0.5,
                                                                                '&:hover': {
                                                                                    backgroundColor: alpha(theme.palette.success.main, 0.08)
                                                                                }
                                                                            }}
                                                                        >
                                                                            <UndoOutlined style={{ fontSize: '15px' }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            ) : (
                                                                <>
                                                                    {/* Edit Image */}
                                                                    {item.type === 'file' && isImageFile(item.name) && allowEditImageBtn && canManipulateItem(item) && (
                                                                        <Tooltip title="Edit Image">
                                                                            <IconButton size="small" disabled={isProcessing} onClick={() => handleEditImage(item)}
                                                                                sx={{ color: theme.palette.text.primary, '&:hover': { color: theme.palette.primary.main } }}
                                                                            >
                                                                                <EditImageIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* Play Video */}
                                                                    {item.type === 'file' && isVideoFile(item.name) && (
                                                                        <Tooltip title="Play Video" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleFileClick(item)}
                                                                                disabled={isProcessing}
                                                                                sx={{
                                                                                    color: theme.palette.primary.main,
                                                                                    '&:hover': {
                                                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <PlayCircleOutlined style={{ fontSize: '18px' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* View PDF */}
                                                                    {shouldShowViewPDF && item.type === 'file' && item.name.toLowerCase().endsWith('.pdf') && (
                                                                        <Tooltip title="View PDF" arrow>
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => handleFileClick(item)}
                                                                                disabled={isProcessing}
                                                                                sx={{
                                                                                    '&:hover': {
                                                                                        backgroundColor: alpha(theme.palette.error.main, 0.08)
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <ModernViewPdfIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* Download */}
                                                                    {item.type === 'file' && (
                                                                        <Tooltip title="Download">
                                                                            <IconButton size="small" onClick={() => handleDownload(item)}
                                                                                disabled={isProcessing}
                                                                                sx={{ color: theme.palette.success.main, '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) } }}
                                                                            >
                                                                                <FileDownloadIcon style={{ fontSize: '18px' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                    {/* Delete */}
                                                                    {allowDelete && canManipulateItem(item) && (
                                                                        <Tooltip title="Delete">
                                                                            <IconButton size="small" onClick={() => handleDeleteFile(item)}
                                                                                disabled={isProcessing}
                                                                                sx={{ color: theme.palette.error.main, '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                                                                            >
                                                                                <DeleteOutlined style={{ fontSize: '15px' }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                </Stack>
                                            );
                                        })}
                                    </React.Fragment>
                                ));
                            })()}
                        </Stack>
                    )}
                </Box>

                {/* Shared Dialogs — driven by CommonFileManagerStateContext */}
                <UpdateFileNotesform />
                <RenameFileForm />
                <DeleteFileDialog />

                {/* Video Preview */}
                {videoPreviewUrl && (
                    <VideoPreview
                        videoUrl={videoPreviewUrl}
                        videoTitle={videoPreviewTitle}
                        onClose={() => { setVideoPreviewUrl(null); setVideoPreviewTitle(''); }}
                    />
                )}

                {/* PDF Preview */}
                {pdfPreviewUrl && (
                    <PdfPreview
                        pdfUrl={pdfPreviewUrl}
                        pdfTitle={pdfPreviewTitle}
                        onClose={() => {
                            setPdfPreviewUrl(null);
                            setPdfPreviewTitle('');
                            setPdfPreviewFile(null);
                        }}
                        onDownload={() => {
                            if (pdfPreviewFile) {
                                handleDownload(pdfPreviewFile);
                            }
                        }}
                    />
                )}

                {/* Image Editor */}
                {imageEditorOpen && fileForEditor && (
                    <ImageEditor
                        open={imageEditorOpen}
                        onClose={() => {
                            setImageEditorOpen(false);
                            setFileForEditor(null);
                            setEditorFileUrl(null);
                        }}
                        imageUrl={editorFileUrl}
                        imageName={fileForEditor.name}
                        onSave={handleImageEditorSave}
                    />
                )}

                {/* Image Preview */}
                {previewOpen && (
                    <ImagePreview
                        isOpen={previewOpen}
                        onClose={() => setPreviewOpen(false)}
                        images={previewImages}
                        currentIndex={previewIndex}
                        onIndexChange={setPreviewIndex}
                        allowEdit={!readonly && allowEdit && module !== FileManagerModule.TASK && module !== FileManagerModule.TASK_NOTE}
                        onRename={(file) => handleEditFileRename(file)}
                        onDownload={(file) => handleDownload(file)}
                    />
                )}
            </Paper>
        </CommonFileManagerStateContext.Provider>
    );
});

export default CompressedFilemanager;

interface CircularProgressWithLabelProps {
    value: number;
    color?: string;
}

function CircularProgressWithLabel({ value, color }: CircularProgressWithLabelProps) {
    const theme = useTheme();
    const activeColor = color || theme.palette.primary.main;
    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', width: 22, height: 22, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress
                variant="determinate"
                value={100}
                sx={{
                    color: alpha(activeColor, 0.12),
                    display: 'block'
                }}
                size={22}
                thickness={4}
            />
            <CircularProgress
                variant="determinate"
                value={value}
                sx={{
                    color: activeColor,
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transition: 'transform 0.2s ease-in-out',
                    display: 'block'
                }}
                size={22}
                thickness={4}
            />
            <Box
                sx={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography
                    variant="caption"
                    component="div"
                    sx={{
                        fontSize: '7px',
                        fontWeight: 700,
                        color: theme.palette.text.secondary,
                        lineHeight: 1,
                        userSelect: 'none',
                        textAlign: 'center'
                    }}
                >
                    {`${Math.round(value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}

interface UploadProgressCircleProps {
    status: 'queued' | 'uploading' | 'completed' | 'failed';
    progress: number;
}

function UploadProgressCircle({ status, progress }: UploadProgressCircleProps) {
    const theme = useTheme();
    const activeColor = theme.palette.primary.main;
    const isQueued = status === 'queued';

    return (
        <Box sx={{ position: 'relative', display: 'inline-flex', width: 32, height: 32, flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
            {/* Background Circle */}
            <CircularProgress
                variant="determinate"
                value={100}
                sx={{
                    color: theme.palette.grey[200],
                    display: 'block'
                }}
                size={32}
                thickness={6}
            />
            {/* Active Progress Circle */}
            {!isQueued && (
                <CircularProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        color: activeColor,
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        transition: 'transform 0.2s ease-in-out',
                        display: 'block'
                    }}
                    size={32}
                    thickness={6}
                />
            )}
            {/* Center Label (Only when uploading/progress is active) */}
            {!isQueued && (
                <Box
                    sx={{
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            fontSize: '9px',
                            fontWeight: 600,
                            color: theme.palette.text.secondary,
                            lineHeight: 1,
                            userSelect: 'none',
                            textAlign: 'center'
                        }}
                    >
                        {`${Math.round(progress)}%`}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
